import {useEffect, useLayoutEffect, useRef, useMemo, useCallback, type ReactNode} from "react";
import { useDebouncedDocumentTitleSuggestion } from "@/hooks/useDebouncedDocumentTitleSuggestion";
import { useLegendState as useState } from "@/hooks/useLegendState";
import { useSelector } from "@legendapp/state/react";
import { batch } from "@legendapp/state";
import { chatMessages$ } from "@/state/chatMessagesLegend";
import {
    Sparkles,
    Copy,
    BookmarkPlus,
    Check,
    Paperclip,
    Globe,
    Brain,
    Shield,
    Zap,
    Bot,
    FileText,
    X,
    Scissors,
    Plus,
    ChevronDown,
    ChevronUp,
    ClipboardPaste,
    FilePlus,
    Tags,
    Link2,
    FileStack,
    Upload,
    ExternalLink,
    MessageSquare,
    MessagesSquare,
    HelpCircle,
    Info,
    HardDrive,
    Trash2,
    Volume2,
    Play,
    Pause,
    Wand2,
    Download,
    Maximize2,
    RefreshCw,
    ListChecks,
    Gauge,
    FolderTree
} from "lucide-react";
import DOMPurify from "dompurify";
import { copyFormattedContent, renderMarkdownToHtml } from "@/utils/markdownUtils";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import {ScrollArea} from "@/components/ui/scroll-area";
import {useToast} from "@/hooks/use-toast";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter
} from "@/components/ui/dialog";
import {Badge} from "@/components/ui/badge";
import {Checkbox} from "@/components/ui/checkbox";
import {ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger} from "@/components/ui/context-menu";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from "@/components/ui/command";
import {Label} from "@/components/ui/label";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {useQuery, useMutation} from "@tanstack/react-query";
import {useMastermind} from "@/contexts/MastermindContext";
import {useLibrary} from "@/contexts/LibraryContext";
import {SpaceWizard} from "@/components/SpaceWizard";
import type { AITool } from "@/types/aiToolkit";
import {queryClient} from "@/lib/queryClient";
import { activeChatContext$, clearActiveChatContext, setActiveChatContext } from "@/state/activeChatContext";
import { chatClipSource$ } from "@/state/chatClipSource";
import { computeChatClipSource } from "@/utils/computeChatClipSource";
import { applyDocumentNamingPreferences, getDocumentNamingPreferencesFromProfile } from "@/lib/documentNamingPreferences";
import {chatThreadsService} from "@/services/chatThreadsService";
import { useThreadSummaries } from "@/contexts/ChatThreadsContext";
import {mastermindSpacesService} from "@/services/mastermindSpacesService";
import {knowledgeBaseService} from "@/services/knowledgeBaseService";
import {tagsService} from "@/services/tagsService";
import { useUserProfile } from "@/contexts/UserProfileContext";
import {topicsService} from "@/services/topicsService";
import {useAuth} from "@/contexts/AuthContext";
import { getCurrentSession } from "@/lib/supabase";
import {useOnboarding} from "@/contexts/OnboardingContext";
import {useWorkspace} from "@/contexts/WorkspaceContext";
import {Link, useSearch, useLocation} from "wouter";
import {PinVerificationDialog} from "@/components/PinVerificationDialog";
import {ShareThreadDialog} from "@/components/ShareThreadDialog";
import {LockedEntityOverlay} from "@/components/EntityLockButton";
import {SelectionToolbar} from "@/components/SelectionToolbar";
import type {MastermindSpace, Source, WebSearchCitation, Topic, Tag, ChatThread, UserProfile} from "@shared/schema";
import {GoogleDrivePicker} from "@/components/GoogleDrivePicker";
import {ChatInput} from "@/components/chat/ChatInput";
import { ChatMessagesLegendList } from "@/components/chat/ChatMessagesLegendList";
import type { ChatMessageListRef } from "@/components/chat/VirtualizedMessageList";
import {UnifiedAttachmentPanel} from "@/components/UnifiedAttachmentPanel";
import {usePersonalizationDetails} from "@/hooks/use-personalization-details";
import { useSourceOfTruth } from "@/hooks/useSourceOfTruth";
import { useChatPreferences } from "@/hooks/useChatPreferences";
import {useChatThread, threadNeedsAutoTitle} from "@/hooks/useChatThread";
import { getChatDraftKey, readChatDraft, writeChatDraft } from "@/lib/chatDraftStorage";
import {useChatSend, getContentType} from "@/hooks/useChatSend";
import {useChatTools} from "@/hooks/useChatTools";
import {useChatTTS} from "@/hooks/useChatTTS";
import {ChatHeader} from "@/components/chat/ChatHeader";
import {
    PendingThreadStatusBanner,
    PendingThreadStatusBadge,
} from "@/components/chat/PendingThreadStatusBanner";
import { getFirstUserMessagePreview, dismissPendingDraft, isPendingDraftDismissed } from "@/lib/pendingThreadStore";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { pushToSongCreator } from "@/services/songCreationSourceService";
import { isSongProductionThread } from "@/lib/isSongProductionThread";
import {ChatEmptyState} from "@/components/chat/ChatEmptyState";
import {MessageBubble} from "@/components/chat/MessageBubble";
import {ActionPlanDialog} from "@/components/chat/ActionPlanDialog";
import {SummarizeArchiveDialog, type SummarizeArchiveOptions} from "@/components/chat/SummarizeArchiveDialog";
import {
    ACTION_PLAN_INSTRUCTION,
    SUMMARIZE_INSTRUCTION,
    deriveTitleFromMarkdown,
    ensureTagIds,
    formatThreadMessagesForSummary,
    parseSummaryAgentResult,
    runThreadActionAgent,
} from "@/lib/threadActionAgents";
import { AgentHandoffBanner } from "@/components/communications/AgentHandoffBanner";
import { getAgentById as getBuiltinAgentById } from "@/data/aiToolkit";
import { getHandoffSession, updateHandoffSession } from "@/lib/agentHandoffSession";
import type { AgentHandoffSession } from "@/types/agentHandoff";
import type { Agent } from "@/types/aiToolkit";
import { aiAgentsService } from "@/services/aiAgentsService";
import { creditsService } from "@/services/creditsService";
import { ragScopeService } from "@/services/ragScopeService";
import { buildProvenanceWorkspaceIds } from "@/lib/ragScopePure";
import {deepResearchService, type ResearchProgress} from "@/services/deepResearchService";
import {DeepResearchProgress} from "@/components/DeepResearchProgress";
import {sourcesService} from "@/services/sourcesService.ts";
import {ThreadLinkingDialog} from "@/components/ThreadLinkingDialog";
import type {WhisperVoiceInputHandle} from "@/components/WhisperVoiceInputButton";
import {ToolsPopover} from "@/components/chat/ToolsPopover";
import {VoiceModeToggle} from "@/components/chat/VoiceModeToggle";
import {VoiceConversationPanel} from "@/components/chat/VoiceConversationPanel";
import {AdvancedSettingsPanel} from "@/components/chat/AdvancedSettingsPanel";
import {UserCog} from "lucide-react";
import {useChatSuggestions} from "@/hooks/useChatSuggestions";
import { ChatWorkspaceErrorBoundary } from "@/components/ChatWorkspaceErrorBoundary";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import RepoDiffPanel from "@/components/RepoDiffPanel";
import RepoFileExplorer from "@/components/RepoFileExplorer";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScreenCaptureButton } from "@/components/screen-capture";
import type { ScreenCaptureMeta } from "@/types/screenCapture";
import { hasAIEditBlock, parseAIEditBlock, stripEditBlock, groupEditsByRepo } from "@/services/repoEditService";
import { getRepoEditTurnContext } from "@/services/repoEditContextStore";
import { validateRepoEdit } from "@/services/repoEditValidator";
import {
    loadPushedRepoEdits,
    savePushedRepoEdit,
    pushPanelKey,
    type RepoPushRecord,
} from "@/services/repoPushStateStore";

interface Message {
    id: string;
    role: "user" | "assistant";
    content: string;
    timestamp: Date;
    citations?: WebSearchCitation[];
    contextCitations?: Record<number, import("@/services/contextOrchestrator").SourceContext>;
    images?: Array<{ url: string; detail?: string }>;
    model?: string;
    autoSelected?: boolean;
    status?: import("@/hooks/useChatSend").ChatMessageStatus;
    errorMessage?: string;
    errorKind?: import("@/lib/chatErrorCatalog").ChatErrorKind;
}

type CreditBalance = {
    balance: string;
    totalEarned: string;
    totalSpent: string;
    subscription: any;
};

function formatRecordingTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
}

export interface ChatWorkspaceProps {
    homeMode?: boolean;
    minimalEmptyState?: boolean;
    className?: string;
    /** DS EDIT 5aug26 — extra icon controls merged into the single chat header row (Home passes search + customize). */
    headerExtras?: ReactNode;
}

export function ChatWorkspace({ homeMode = false, minimalEmptyState = false, className, headerExtras }: ChatWorkspaceProps = {}) {
    // Get URL search params for deep linking from Spaces/Topics
    const searchString = useSearch();
    const [location, setWouterLocation] = useLocation();
    const { completeMilestone, hasMilestone } = useOnboarding();

    const { currentWorkspaceId } = useWorkspace();
    const {user} = useAuth();
    const messages = useSelector(chatMessages$);
    const activeChatSpaceId = useSelector(activeChatContext$.spaceId);
    const messagesRef = useRef<Message[]>([]); // Track latest messages for persistence
    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);
    // Helper to update Legend observable + ref (passed to useChatThread / useChatSend)
    const updateMessages = useCallback((updater: React.SetStateAction<Message[]>) => {
        batch(() => {
            const prev = chatMessages$.peek() as Message[];
            const next = typeof updater === "function" ? (updater as (p: Message[]) => Message[])(prev) : updater;
            chatMessages$.set(next);
            messagesRef.current = next;
        });
    }, []);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messageListRef = useRef<ChatMessageListRef | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const submitButtonRef = useRef<HTMLButtonElement>(null);
    const [input, setInput] = useState("");
    const inputRef = useRef<string>("");
    const inputDebounceRef = useRef<number | null>(null);
    const [fileAttached, setFileAttached] = useState<Array<{ type: string; data: string; filename?: string; meta?: ScreenCaptureMeta }>>([]);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [dismissedDiffPanels, setDismissedDiffPanels] = useState<Set<string>>(() => new Set());
    const [pushedRepoEdits, setPushedRepoEdits] = useState<Record<string, RepoPushRecord>>({});
    const editBlockedToastShownRef = useRef<Set<string>>(new Set());
    const [researchProgress, setResearchProgress] = useState<ResearchProgress | null>(null);
    const [isResearching, setIsResearching] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const initialInputRef = useRef<string>("");  // Stores text that was in input before recording started
    const finalTranscriptRef = useRef<string>("");  // Stores final transcript for current recording
    const shouldSubmitAfterRecordingRef = useRef<boolean>(false);
    const submitAfterRecordingFallbackTimeoutRef = useRef<number | null>(null);
    const whisperVoiceRef = useRef<WhisperVoiceInputHandle>(null);
    const appliedQueryParamRef = useRef(false);
    
    const removeDuplicateWords = useCallback((text: string) => {
        // Fix: Remove any word that repeats immediately, case-insensitive
        // e.g. "You you" -> "You", "the the" -> "the"
        return text.replace(/\b(\w+)(\s+\1\b)+/gi, "$1");
    }, []);

    const removeRepeatedPhrases = useCallback((text: string) => {
        let words = text.trim().split(/\s+/);
        if (words.length < 2) return text.trim();

        // Aggressively find and remove any repeated sequence of 2 or more words
        // e.g. "wanna be a wanna be a" -> "wanna be a"
        let changed = true;
        while (changed) {
            changed = false;
            // Try phrase lengths from half the total words down to 2 ..
            for (let len = Math.floor(words.length / 2); len >= 2; len--) {
                for (let i = 0; i <= words.length - 2 * len; i++) {
                    const phrase1 = words.slice(i, i + len).join(" ").toLowerCase();
                    const phrase2 = words.slice(i + len, i + 2 * len).join(" ").toLowerCase();
                    
                    if (phrase1 === phrase2) {
                        // Found a repeated phrase! Remove the duplicate
                        words.splice(i + len, len);
                        changed = true;
                        break;
                    }
                }
                if (changed) break;
            }
        }
        return words.join(" ");
    }, []);

    const cleanSpeech = useCallback((text: string) => {
        if (!text || !text.trim()) return "";
        
        // 1. Initial cleanup
        let cleaned = text.trim();

        // 2. Remove duplicate adjacent words ("you you")
        cleaned = removeDuplicateWords(cleaned);

        // 3. Remove repeated phrases of any length ("wanna be a wanna be a")
        cleaned = removeRepeatedPhrases(cleaned);

        // 4. Final formatting: trim and capitalize
        cleaned = cleaned.trim();
        if (!cleaned) return "";
        
        return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    }, [removeDuplicateWords, removeRepeatedPhrases]);
    const [conversationMode, setConversationMode] = useState(false);
    const [selectedAiToolkit, setSelectedAiToolkit] = useState<AITool | null>(null);
    const [pendingRecoveryDraft, setPendingRecoveryDraft] = useState<import("@/lib/pendingThreadStore").PendingThreadDraft | null>(null);

    const {
        currentThread: thread,
        currentThreadId,
        setCurrentThreadId,
        createThreadMutation,
        updateThreadMutation,
        generateMetadataMutation,
        shouldAutoTitle,
        lastLoadedThreadIdRef,
    } = useChatThread({
        updateMessages: updateMessages as (u: any) => void,
        threadIdFromUrl: (() => {
            const params = new URLSearchParams(searchString || "");
            return params.get("threadId");
        })(),
        spaceIdFromUrl: (() => {
            const params = new URLSearchParams(searchString || "");
            return params.get("spaceId");
        })(),
        selectedSpaceId: activeChatSpaceId ?? null,
        userId: user?.id ?? null,
        onReconcileDraft: (draft) => setPendingRecoveryDraft(draft),
    });

    const draftLoadedForKeyRef = useRef<string | null>(null);

    const setInputWithDraft = useCallback(
        (value: React.SetStateAction<string>) => {
            setInput((prev) => {
                const next = typeof value === "function" ? value(prev) : value;
                inputRef.current = next;
                writeChatDraft(getChatDraftKey(currentWorkspaceId, currentThreadId), next);
                return next;
            });
        },
        [currentWorkspaceId, currentThreadId]
    );

    useEffect(() => {
        const key = getChatDraftKey(currentWorkspaceId, currentThreadId);
        if (draftLoadedForKeyRef.current === key) return;
        draftLoadedForKeyRef.current = key;
        const draft = readChatDraft(key);
        setInput(draft);
        inputRef.current = draft;
    }, [currentWorkspaceId, currentThreadId]);

    const tools = useChatTools({
        currentThreadId,
        currentThread: thread,
        updateThreadMutation,
    });
    const {
        modelSelectorOpen,
        setModelSelectorOpen,
        toolsMenuOpen,
        setToolsMenuOpen,
        selectedModel,
        setSelectedModel,
        modelInfo,
        setModelInfo,
        selectedAgentId,
        setSelectedAgentId,
        selectedSuperPrompt,
        setSelectedSuperPrompt,
        selectedWorkflow,
        setSelectedWorkflow,
        webSearchMode,
        setWebSearchMode,
        thinkingMode,
        setThinkingMode,
        autoSelectBest,
        setAutoSelectBest,
        deepResearchMode,
        setDeepResearchMode,
        contextMode,
        setContextMode,
        chatMode,
        setChatMode,
        selectedSpaceIds,
        setSelectedSpaceIds,
        selectedTopicId,
        setSelectedTopicId,
        useFullScope,
        setUseFullScope,
        selectedSourceIds,
        setSelectedSourceIds,
        selectedDocumentIds,
        setSelectedDocumentIds,
        selectedGitHubRepo,
        setSelectedGitHubRepo,
        selectedGitHubRepos,
        toggleGitHubRepo,
        maxAttachedRepos,
        cachedRepoContext,
        cachedRepoContexts,
        cachedRepoContextRef,
        isLoadingRepoContext,
        repoIndexingProgress,
        repoIndexingProgressMap,
        refreshRepoIndexForSelection,
        patchCachedRepoFileContent,
        availableModels,
        refetchModels,
        isRefreshingModels,
        isLoadingModels,
        isModelsFetchError,
        modelsFetchError,
        modelsLastUpdated,
    } = tools;

    const [spaceDialogOpen, setSpaceDialogOpen] = useState(false);
    const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
    const [spaceWizardOpen, setSpaceWizardOpen] = useState(false);
    const [isImgUploading, setImgUploading] = useState(false);
    const [customToolDialogOpen, setCustomToolDialogOpen] = useState(false);
    const [showPromptEnhancer, setShowPromptEnhancer] = useState(false);
    const [customTools, setCustomTools] = useState<Array<{ id: string; name: string; description: string }>>([]);
    const [newToolName, setNewToolName] = useState("");
    const [newToolDescription, setNewToolDescription] = useState("");
    const [savingToKnowledge, setSavingToKnowledge] = useState<string | null>(null);
    const [newDocDialogOpen, setNewDocDialogOpen] = useState(false);
    const [newDocContent, setNewDocContent] = useState("");
    const [newDocTitle, setNewDocTitle] = useState("");
    const [newDocSpaceId, setNewDocSpaceId] = useState<string | null>(null);
    const [newDocTopicId, setNewDocTopicId] = useState<string | null>(null);
    const [newDocTagIds, setNewDocTagIds] = useState<string[]>([]);
    const [saveKnowledgeDialogOpen, setSaveKnowledgeDialogOpen] = useState(false);
    const [saveKnowledgeContent, setSaveKnowledgeContent] = useState("");
    const [saveKnowledgeMessageId, setSaveKnowledgeMessageId] = useState<string | null>(null);
    const [saveKnowledgeTagSearch, setSaveKnowledgeTagSearch] = useState("");
    // Supabase-backed user chat preferences (survive logout/device switch).
    const { preferences: chatPrefs, updatePreference: updateChatPreference } = useChatPreferences();
    const useRAG = chatPrefs.useRag;
    const setUseRAG = useCallback(
        (value: boolean) => updateChatPreference("useRag", value),
        [updateChatPreference]
    );
    const voiceMode = chatPrefs.voiceMode;
    const setVoiceMode = useCallback(
        (mode: typeof chatPrefs.voiceMode) => updateChatPreference("voiceMode", mode),
        [updateChatPreference]
    );
    const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
    const [repoExplorerOpen, setRepoExplorerOpen] = useState(false);
    // Explicit UI selections also update the user's cross-device defaults;
    // thread-level restores go through the plain setters and stay thread-scoped.
    const setChatModeAndDefault = useCallback(
        (mode: "normal" | "questioning" | "dialogue") => {
            setChatMode(mode);
            updateChatPreference("chatMode", mode);
        },
        [setChatMode, updateChatPreference]
    );
    const setContextModeAndDefault = useCallback(
        (mode: "MAX" | "LITE") => {
            setContextMode(mode);
            updateChatPreference("contextMode", mode);
        },
        [setContextMode, updateChatPreference]
    );
    const setSelectedAgentIdAndDefault = useCallback(
        (agentId: string | null) => {
            setSelectedAgentId(agentId);
            updateChatPreference("preferredAgentId", agentId);
        },
        [setSelectedAgentId, updateChatPreference]
    );
    const setSelectedSuperPromptAndDefault = useCallback(
        (prompt: typeof selectedSuperPrompt) => {
            setSelectedSuperPrompt(prompt);
            updateChatPreference("selectedSuperPromptId", prompt?.id ?? null);
        },
        [setSelectedSuperPrompt, updateChatPreference]
    );
    const [handoffSession, setHandoffSession] = useState<AgentHandoffSession | null>(null);
    const [handoffAgent, setHandoffAgent] = useState<Agent | null>(null);
    const appliedHandoffRef = useRef<string | null>(null);
    const appliedAgentParamRef = useRef<string | null>(null);
    const [saveKnowledgeTitle, setSaveKnowledgeTitle] = useState("");
    const [isSaveTagDropdownOpen, setIsSaveTagDropdownOpen] = useState(false);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editedTitle, setEditedTitle] = useState("");
    const [showMetadataPanel, setShowMetadataPanel] = useState(false);
    const [tagInputValue, setTagInputValue] = useState("");
    const [isTagPopoverOpen, setIsTagPopoverOpen] = useState(false);
    const [isSpacePopoverOpen, setIsSpacePopoverOpen] = useState(false);
    const [isTopicPopoverOpen, setIsTopicPopoverOpen] = useState(false);
    const [spaceSearchQuery, setSpaceSearchQuery] = useState("");
    const [topicSearchQuery, setTopicSearchQuery] = useState("");
    const [newSpaceName, setNewSpaceName] = useState("");
    const [newTopicName, setNewTopicName] = useState("");
    const [isCreatingSpace, setIsCreatingSpace] = useState(false);
    const [isCreatingTopic, setIsCreatingTopic] = useState(false);
    const [isPinDialogOpen, setIsPinDialogOpen] = useState(false);
    const newDocCreatedAtRef = useRef<Date | null>(null);
    const saveKnowledgeBaseTitleRef = useRef("Untitled Document");
    const saveKnowledgeCreatedAtRef = useRef<Date | null>(null);
    const [deleteThreadConfirmOpen, setDeleteThreadConfirmOpen] = useState(false);
    const [isDeletingThread, setIsDeletingThread] = useState(false);
    const [showDrivePicker, setShowDrivePicker] = useState(false);
    const [threadLinkingDialogOpen, setThreadLinkingDialogOpen] = useState(false);
    const [shareThreadDialogOpen, setShareThreadDialogOpen] = useState(false);
    const [actionPlanDialogOpen, setActionPlanDialogOpen] = useState(false);
    const [summarizeDialogOpen, setSummarizeDialogOpen] = useState(false);
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);
    const [fullscreenImage, setFullscreenImage] = useState<{ url: string; alt: string } | null>(null);
    const usePersonalization = chatPrefs.usePersonalization;
    const setUsePersonalization = useCallback(
        (value: boolean) => updateChatPreference("usePersonalization", value),
        [updateChatPreference]
    );
    const sourceOfTruthOn = chatPrefs.useSourceOfTruth;
    const setSourceOfTruthOn = useCallback(
        (value: boolean) => updateChatPreference("useSourceOfTruth", value),
        [updateChatPreference]
    );
    const { docIds: sourceOfTruthDocIds, hasCanon: hasSourceOfTruthCanon } = useSourceOfTruth();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Track threads that were unlocked in this session (for auto-relock on exit)
    const unlockedThreadIdsRef = useRef<Set<string>>(new Set());

    const [serverPersonalization, setServerPersonalization] = useState<{
        userVoice: boolean;
        agent: { name: string; lens: string | null } | null;
        space: { names: string[]; hasLens: boolean } | null;
        topic: { name: string } | null;
        sources: { count: number } | null;
        chatMode: string;
    } | null>(null);
    const {toast} = useToast();
    const { clipToSidebar, setIsCollapsed, setCurrentSpaceId, setCurrentTopicId } = useMastermind();
    const { setSelectedDocumentId, setLibrarySidebarOpen } = useLibrary();
    const { profile: userProfile } = useUserProfile();

    // Track personalization context for indicator - use server data if available, else compute locally
    const personalizationDetails = usePersonalizationDetails({
        selectedAgentId,
        selectedSourceIds,
        selectedSpaceIds,
        selectedTopicId,
        chatMode,
        serverLayers: serverPersonalization,
        sourceOfTruthCount: sourceOfTruthOn && hasSourceOfTruthCanon ? sourceOfTruthDocIds.length : 0,
    });

    // Track the last processed search string to detect new navigation (not just processed flag)
    const lastProcessedSearchString = useRef<string>("");
    // Track topicId from URL navigation to ensure it's preserved when creating threads
    const topicIdFromUrlRef = useRef<string | null>(null);
    // Track previous model for Deep Research toggle
    const previousModelRef = useRef<string>(selectedModel);

    // Mastermind space/topic mirrors chat tools + preserved URL topic so DocumentCreator / library open-item see context on /chat.
    useEffect(() => {
        const spaceId = selectedSpaceIds[0] ?? null;
        const topicId = selectedTopicId ?? topicIdFromUrlRef.current ?? null;
        setCurrentSpaceId(spaceId);
        setCurrentTopicId(topicId);
    }, [selectedSpaceIds, selectedTopicId, setCurrentSpaceId, setCurrentTopicId]);

    useEffect(() => {
        return () => {
            setCurrentSpaceId(null);
            setCurrentTopicId(null);
            clearActiveChatContext();
            chatClipSource$.set({ threadId: null, sourceUrl: null, sourceLabel: null });
        };
    }, [setCurrentSpaceId, setCurrentTopicId]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        chatClipSource$.set(
            computeChatClipSource({
                searchString: searchString ?? "",
                threadId: currentThreadId,
                threadTitle: thread?.title ?? null,
                threadTopic: thread?.topic ?? null,
                pageHref: window.location.href,
            })
        );
    }, [searchString, currentThreadId, thread?.title, thread?.topic]);

    const {
        speakingMessageId,
        isSpeechPaused,
        availableVoices,
        selectedVoiceUri,
        speechRate,
        setSelectedVoiceUri,
        setSpeechRate,
        ttsVoices,
        selectedTtsVoiceId,
        setSelectedTtsVoiceId,
        ttsLoadingMessageId,
        ttsErrorMessageId,
        ttsErrorMessage,
        speak,
        pause,
        resume,
    } = useChatTTS();

    // TTS voice/speed preferences are persisted inside useChatTTS (debounced).

    useEffect(() => {
        if (!homeMode) return;
        setCurrentThreadId(null);
        lastLoadedThreadIdRef.current = null;
        updateMessages([]);
    }, [homeMode, setCurrentThreadId, updateMessages, lastLoadedThreadIdRef]);

    // Auto-switch model when Deep Research toggle changes
    useEffect(() => {
        if (deepResearchMode) {
            // Deep Research enabled: save current model and switch to Sonar Pro
            previousModelRef.current = selectedModel;
            console.log(`[Deep Research] Switching model from ${selectedModel} to perplexity/sonar-pro`);
            setSelectedModel("perplexity/sonar-pro");
        } else {
            // Deep Research disabled: restore to default Claude Sonnet 4
            console.log(`[Deep Research] Switching model back to anthropic/claude-sonnet-4`);
            setSelectedModel("anthropic/claude-sonnet-4");
        }
    }, [deepResearchMode, setSelectedModel]);

    // Keep URL in sync when a thread is active so refresh preserves the open thread
    useEffect(() => {
        if (homeMode || !currentThreadId || location !== "/chat") return;

        const params = new URLSearchParams(searchString || "");
        if (params.get("threadId") === currentThreadId) return;
        if (params.get("spaceId")) return;

        params.set("threadId", currentThreadId);
        const query = params.toString();
        setWouterLocation(query ? `/chat?${query}` : "/chat", { replace: true });
    }, [homeMode, currentThreadId, location, searchString, setWouterLocation]);

    // Process URL query params for deep linking from Spaces/Topics
    useEffect(() => {
        if (homeMode) return;
        if (searchString === lastProcessedSearchString.current) return;

        const params = new URLSearchParams(searchString || '');
        const urlSpaceId = params.get('spaceId');
        const urlTopicId = params.get('topicId');
        const urlThreadId = params.get('threadId');
        // Plain /chat with no threadId or spaceId = show clean new chat (e.g. from Home "New Chat")
        // Do NOT call clearActiveChatContext() here: after a space/topic deep link we intentionally strip the URL
        // to `/chat` while keeping tools + topicIdFromUrlRef + activeChatContext$. Clearing Legend on empty search
        // would drop topicId and break DocumentCreator auto-link to the topic.
        if (!urlThreadId && !urlSpaceId) {
            lastProcessedSearchString.current = searchString ?? '';

            const hasActiveSpaceContext = !!topicIdFromUrlRef.current || !!activeChatSpaceId;
            const savedThreadId =
                typeof window !== 'undefined' ? localStorage.getItem('currentChatThreadId') : null;
            if (savedThreadId && !hasActiveSpaceContext) {
                setWouterLocation(`/chat?threadId=${encodeURIComponent(savedThreadId)}`, { replace: true });
                return;
            }

            setCurrentThreadId(null);
            lastLoadedThreadIdRef.current = null;
            updateMessages([]);
            return;
        }

        // Mark as processed before state changes
        lastProcessedSearchString.current = searchString;

        // Handle direct thread loading
        if (urlThreadId) {
            const switchingThread = urlThreadId !== currentThreadId;
            setCurrentThreadId(urlThreadId);
            if (switchingThread) {
                lastLoadedThreadIdRef.current = null;
                updateMessages([]);
            }
            return;
        }

        if (urlSpaceId) {
                // Start a NEW chat with space/topic context - clear any existing thread
                setCurrentThreadId(null);
                lastLoadedThreadIdRef.current = null;
                updateMessages([]);

                setSelectedSpaceIds([urlSpaceId]);
                setToolsMenuOpen(true); // Auto-expand tools to show the selection
                setActiveChatContext({spaceId: urlSpaceId, topicId: urlTopicId || null});

                if (urlTopicId) {
                    setSelectedTopicId(urlTopicId);
                    topicIdFromUrlRef.current = urlTopicId;
                } else {
                    setSelectedTopicId(null);
                }

                // Clear URL params after processing to prevent stale state on refresh
                const preservedTopicId = topicIdFromUrlRef.current;
                setTimeout(() => {
                    setWouterLocation('/chat', {replace: true});
                    if (preservedTopicId) {
                        topicIdFromUrlRef.current = preservedTopicId;
                    }
                    // Re-apply Legend after strip (same as URL params for this navigation — avoids stale ref if ?spaceId= only)
                    setActiveChatContext({
                        spaceId: urlSpaceId,
                        topicId: urlTopicId,
                    });
                }, 0);
        }
    }, [searchString, setWouterLocation, activeChatSpaceId, currentThreadId, setCurrentThreadId, updateMessages, lastLoadedThreadIdRef, setSelectedSpaceIds, setToolsMenuOpen, setSelectedTopicId]);

    // Pre-fill input from ?q= (e.g. from Universal Search "Ask in Chat")
    useEffect(() => {
        if (!searchString || appliedQueryParamRef.current) return;
        const params = new URLSearchParams(searchString);
        const q = params.get('q');
        if (q && q.trim()) {
            appliedQueryParamRef.current = true;
            setInput(decodeURIComponent(q.trim()));
            setWouterLocation('/chat', { replace: true });
        }
    }, [searchString, setWouterLocation]);

    // Agent handoff from integration pages (?handoff= & ?agent=)
    useEffect(() => {
        if (!searchString || homeMode) return;
        const params = new URLSearchParams(searchString);
        const handoffId = params.get("handoff");
        const agentId = params.get("agent");

        if (handoffId && appliedHandoffRef.current !== handoffId) {
            appliedHandoffRef.current = handoffId;
            const session = getHandoffSession(handoffId);
            if (session) {
                setHandoffSession(session);
                setUseRAG(true);
                void (async () => {
                    const agent =
                        getBuiltinAgentById(session.agentId) ??
                        (await aiAgentsService.getAgentById(session.agentId));
                    setHandoffAgent(agent);
                    if (agent) setSelectedAgentId(agent.id);
                })();

                const seedKey = `agent-handoff-seed:${handoffId}`;
                const seed =
                    typeof sessionStorage !== "undefined"
                        ? sessionStorage.getItem(seedKey)
                        : null;
                if (seed?.trim()) {
                    setInputWithDraft(seed);
                }
            }
            setWouterLocation("/chat", { replace: true });
            return;
        }

        if (agentId && !handoffId && appliedAgentParamRef.current !== agentId) {
            appliedAgentParamRef.current = agentId;
            void (async () => {
                const agent =
                    getBuiltinAgentById(agentId) ?? (await aiAgentsService.getAgentById(agentId));
                if (agent) setSelectedAgentId(agent.id);
            })();
            setWouterLocation("/chat", { replace: true });
        }
    }, [searchString, homeMode, setWouterLocation, setSelectedAgentId, setInputWithDraft]);

    useEffect(() => {
        if (handoffSession && currentThreadId) {
            updateHandoffSession(handoffSession.id, { chatThreadId: currentThreadId });
        }
    }, [handoffSession, currentThreadId]);

    const handleNearEndChange = useCallback((nearEnd: boolean) => {
        setShowScrollToBottom(!nearEnd);
    }, []);

    useEffect(() => {
        if (messages.length === 0) {
            setShowScrollToBottom(false);
        }
    }, [messages.length]);

    const scrollToBottom = useCallback(() => {
        void messageListRef.current?.scrollToEnd({ animated: true });
    }, []);

    // Track previous thread ID for auto-relock on thread change
    const prevThreadIdRef = useRef<string | null>(null);

    // Auto-relock previous thread when switching threads
    useEffect(() => {
        const prevThreadId = prevThreadIdRef.current;

        // If we're switching away from an unlocked thread, re-lock it
        if (prevThreadId && prevThreadId !== currentThreadId && unlockedThreadIdsRef.current.has(prevThreadId)) {
            chatThreadsService.lockChatThread(prevThreadId, true, currentWorkspaceId ?? null).catch(err => {
                console.error("Failed to auto-relock thread on switch:", err);
            });
            unlockedThreadIdsRef.current.delete(prevThreadId);
        }

        prevThreadIdRef.current = currentThreadId;
    }, [currentThreadId, currentWorkspaceId]);

    useEffect(() => {
        setPushedRepoEdits(loadPushedRepoEdits(currentThreadId));
    }, [currentThreadId]);

    // Auto-relock threads on component unmount
    useEffect(() => {
        return () => {
            // Re-lock any threads that were unlocked during this session
            unlockedThreadIdsRef.current.forEach((threadId) => {
                chatThreadsService.lockChatThread(threadId, true, currentWorkspaceId ?? null).catch(err => {
                    console.error("Failed to auto-relock thread:", err);
                });
            });
            unlockedThreadIdsRef.current.clear();
        };
    }, []);

    const { personalizedSuggestions, chatGreeting, suggestionsLoading } = useChatSuggestions(user);

    // PIN status query
    const {data: pinStatus} = useQuery<{ hasPinSet: boolean; isSessionValid: boolean }>({
        queryKey: ["/api/pin/status"],
        queryFn: async () => {
            const { pinService } = await import("@/services/pinService");
            return pinService.getStatus();
        },
        staleTime: 1000 * 60 * 10, // Cache for 10 minutes
        refetchOnWindowFocus: false,
    });

    // Toggle thread lock mutation
    const toggleLockMutation = useMutation({
        mutationFn: async ({threadId, lock}: { threadId: string; lock: boolean }) => {
            const result = await chatThreadsService.lockChatThread(threadId, lock, currentWorkspaceId ?? null);
            if (!result) {
                throw new Error("Failed to toggle lock - thread not found");
            }
            return result;
        },
        onSuccess: (_result, {threadId, lock}) => {
            queryClient.invalidateQueries({queryKey: ["/api/chat-threads"]});
            toast({
                title: lock ? "Thread Locked" : "Thread Unlocked",
                description: lock ? "This thread is now protected with your PIN" : "Thread is now accessible",
            });
            if (!lock) {
                unlockedThreadIdsRef.current.add(threadId);
            } else {
                unlockedThreadIdsRef.current.delete(threadId);
            }
        },
        onError: (error: Error) => {
            toast({
                title: "Error",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    // Handler for lock button click
    const handleLockClick = () => {
        if (!currentThreadId) return;

        // Check if PIN is set
        if (!pinStatus?.hasPinSet) {
            toast({
                title: "PIN Required",
                description: "Please set a security PIN in Settings first",
            });
            setWouterLocation("/settings");
            return;
        }

        // Check if session is valid
        if (!pinStatus?.isSessionValid) {
            setIsPinDialogOpen(true);
            return;
        }

        // Toggle lock
        const isLocked = thread?.isLocked === "true" || thread?.isLocked === true;
        toggleLockMutation.mutate({threadId: currentThreadId, lock: !isLocked});
    };

    // Fetch all spaces for the logged-in user (workspace-scoped)
    const {data: spaces = []} = useQuery<MastermindSpace[]>({
        queryKey: ['/api/spaces', user?.id, currentWorkspaceId ?? 'personal'],
        queryFn: async () => {
            if (!user) return [];
            return await mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null);
        },
        enabled: !!user,
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
        refetchOnWindowFocus: false,
    });

    const shouldLoadSources = !!user && (
        sourceDialogOpen ||
        selectedSourceIds.length > 0 ||
        selectedSpaceIds.length > 0
    );

    const shouldLoadTags = !!user && (
        isTagPopoverOpen ||
        newDocDialogOpen ||
        saveKnowledgeDialogOpen
    );

    const shouldLoadThreadSummaries = threadLinkingDialogOpen;

    const shouldLoadAllTopics = !!user && (
        newDocDialogOpen ||
        saveKnowledgeDialogOpen ||
        newDocSpaceId != null ||
        showMetadataPanel
    );

    // Fetch all sources (for linked source details, workspace-scoped)
    const {data: sources = [], isLoading: sourcesLoading} = useQuery<Source[]>({
        queryKey: ['/api/sources', user?.id, currentWorkspaceId ?? null],
        queryFn: async () => {
            if (!user) return [];
            try {
                return await sourcesService.getSources(undefined, currentWorkspaceId ?? null);
            } catch (error) {
                console.error("Failed to fetch sources:", error);
                return [];
            }
        },
        enabled: shouldLoadSources,
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
        refetchOnWindowFocus: false,
    });

    // Fetch all tags for document linking
    const {data: allTags = []} = useQuery<Tag[]>({
        queryKey: ['/api/tags', user?.id],
        queryFn: async () => {
            if (!user) return [];
            return await tagsService.getTags();
        },
        enabled: shouldLoadTags,
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
        refetchOnWindowFocus: false,
    });

    // Fetch all thread summaries to collect unique tags used across threads (lightweight - no messages)
    const { threads: allThreads } = useThreadSummaries({ enabled: shouldLoadThreadSummaries });

    // Merge tags from tags table with unique tag names from all threads
    const mergedTagNames = useMemo(() => {
        const tagNamesFromTable = new Set(allTags.map(t => t.name));
        const tagNamesFromThreads = new Set<string>();

        allThreads.forEach(thread => {
            if (thread.tags && Array.isArray(thread.tags)) {
                thread.tags.forEach(tag => {
                    if (tag && typeof tag === 'string') {
                        tagNamesFromThreads.add(tag);
                    }
                });
            }
        });

        // Combine both sets
        const tableTags = Array.from(tagNamesFromTable);
        const threadTags = Array.from(tagNamesFromThreads);
        const combined = new Set([...tableTags, ...threadTags]);
        return Array.from(combined).sort((a, b) => a.localeCompare(b));
    }, [allTags, allThreads]);

    // Fetch all topics for new document linking
    const {data: allTopics = []} = useQuery<Topic[]>({
        queryKey: ['/api/topics'],
        queryFn: async () => {
            const { topicsService } = await import("@/services/topicsService");
            return topicsService.getAllTopics();
        },
        enabled: shouldLoadAllTopics,
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
        refetchOnWindowFocus: false,
    });

    // Fetch topics for selected spaces (for topic selection)
    const {data: spaceTopics = [], isFetching: isTopicsFetching} = useQuery<Topic[]>({
        queryKey: ['/api/topics', 'by-spaces', selectedSpaceIds],
        queryFn: async () => {
            if (selectedSpaceIds.length === 0) return [];
            // Fetch topics for all selected spaces
            const allTopics: Topic[] = [];
            for (const spaceId of selectedSpaceIds) {
                try {
                    const topics = await topicsService.getTopics(spaceId);
                    allTopics.push(...topics);
                } catch (error) {
                    console.error(`Failed to fetch topics for space ${spaceId}:`, error);
                }
            }
            return allTopics;
        },
        enabled: selectedSpaceIds.length > 0,
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
        refetchOnWindowFocus: false,
    });

    const getTopicTitleById = useCallback((topicId: string | null | undefined): string | null => {
        if (!topicId) return null;
        const topic =
            (Array.isArray(spaceTopics) ? spaceTopics : []).find((t) => t.id === topicId) ??
            (Array.isArray(allTopics) ? allTopics : []).find((t) => t.id === topicId);
        const name = topic?.name?.trim();
        return name || null;
    }, [spaceTopics, allTopics]);

    const chatDocumentNamingPreferences = useMemo(
        () => getDocumentNamingPreferencesFromProfile(userProfile),
        [userProfile]
    );
    const chatDocumentTopicTitle = getTopicTitleById(newDocTopicId ?? selectedTopicId ?? topicIdFromUrlRef.current ?? null);

    const { syncTitleRef, onUserEditedTitle, resetSuggestionState } = useDebouncedDocumentTitleSuggestion({
        contentHtml: newDocContent,
        setTitle: setNewDocTitle,
        enabled: newDocDialogOpen,
        topicTitle: chatDocumentTopicTitle,
        createdAt: newDocCreatedAtRef.current,
        debounceMs: 1000,
    });

    useEffect(() => {
        syncTitleRef(newDocTitle);
    }, [newDocTitle, syncTitleRef]);

    useEffect(() => {
        if (newDocDialogOpen) {
            resetSuggestionState();
        }
    }, [newDocDialogOpen, resetSuggestionState]);

    useEffect(() => {
        if (!saveKnowledgeDialogOpen) return;
        setSaveKnowledgeTitle(
            applyDocumentNamingPreferences(
                saveKnowledgeBaseTitleRef.current,
                {
                    topicTitle: getTopicTitleById(newDocTopicId),
                    createdAt: saveKnowledgeCreatedAtRef.current,
                    preferences: chatDocumentNamingPreferences,
                }
            )
        );
    }, [saveKnowledgeDialogOpen, newDocTopicId, getTopicTitleById, chatDocumentNamingPreferences]);

    // Track previous space IDs to detect actual space changes vs initial load
    const prevSpaceIdsRef = useRef<string[]>([]);

    // Clear selected topic when spaces change (topic might not belong to new space selection)
    useEffect(() => {
        if (selectedSpaceIds.length === 0) {
            setSelectedTopicId(null);
            topicIdFromUrlRef.current = null; // Clear ref when spaces are cleared
            prevSpaceIdsRef.current = [];
            return;
        }

        if (isTopicsFetching) return;

        const prevSpaceIds = prevSpaceIdsRef.current;
        const hasURLNavigationTopic = selectedTopicId &&
            (lastProcessedSearchString.current.includes('topicId=') ||
             prevSpaceIds.length === 0);

        const spacesActuallyChanged = !hasURLNavigationTopic && prevSpaceIds.length > 0 &&
            (prevSpaceIds.length !== selectedSpaceIds.length ||
             !prevSpaceIds.every(id => selectedSpaceIds.includes(id)));

        prevSpaceIdsRef.current = [...selectedSpaceIds];

        if (selectedTopicId && spacesActuallyChanged) {
            const topicStillValid = spaceTopics.some(t => t.id === selectedTopicId);
            if (!topicStillValid) {
                setSelectedTopicId(null);
                // Only clear URL ref if topic is not from URL navigation
                if (!hasURLNavigationTopic) {
                    topicIdFromUrlRef.current = null;
                }
            }
        }
    }, [selectedSpaceIds, spaceTopics, selectedTopicId, isTopicsFetching]);


    // Fetch credit balance for low balance warnings
    const {data: creditData} = useQuery<CreditBalance>({
        queryKey: ['/api/credits'],
        queryFn: () => creditsService.getOrCreateUserCredits(),
        refetchInterval: 30000, // Refetch every 30 seconds to keep balance updated
        staleTime: 1000 * 20, // Consider data stale after 20 seconds
        refetchOnWindowFocus: false,
    });

    // Check if user has personalization data available
    const hasPersonalizationData = useMemo(() => {
        if (!userProfile) return false;
        const hasBackground = userProfile.background && userProfile.background.trim().length > 0;
        const hasWorldview = userProfile.worldview && userProfile.worldview.trim().length > 0;
        const hasGoals = userProfile.goals && Array.isArray(userProfile.goals) && userProfile.goals.length > 0;
        const hasResumeText = (userProfile as any).resume_text && (userProfile as any).resume_text.trim().length > 0;
        return hasBackground || hasWorldview || hasGoals || hasResumeText;
    }, [userProfile]);

    const creditBalance = parseInt(creditData?.balance || "0");
    const showLowBalanceWarning = creditBalance > 0 && creditBalance < 100;

    // Curated model categories with cost tiers
    // Note: 'vision' category = models that can analyze images in chat
    type ModelCategory = 'popular' | 'general' | 'creative' | 'fast' | 'reasoning' | 'code' | 'thinking' | 'vision';
    type CostTier = 'low' | 'medium' | 'high';

    interface CuratedModel {
        id: string;
        name: string;
        category: ModelCategory;
        costTier: CostTier;
        supportsThinking?: boolean;
        supportsVision?: boolean;
        description?: string;
    }

    const curatedModels: CuratedModel[] = useMemo(() => [
        { id: 'x-ai/grok-4.1-fast', name: 'Grok 4.1 Fast', category: 'popular', costTier: 'low', description: 'Fastest (455 tok/s), 2M context' },
        { id: 'google/gemini-2.5-pro', name: 'Gemini 2.5 Pro', category: 'popular', costTier: 'medium', description: '1M context, great reasoning' },
        { id: 'anthropic/claude-sonnet-4', name: 'Claude Sonnet 4', category: 'popular', costTier: 'medium', description: 'Best coding (80.9% SWE-bench)' },
        { id: 'anthropic/claude-opus-4', name: 'Claude Opus 4', category: 'popular', costTier: 'high', description: 'Most capable, deep reasoning' },
        { id: 'openai/gpt-4o', name: 'GPT-4o', category: 'general', costTier: 'medium', supportsVision: true, description: 'Polished, strong all-rounder' },
        { id: 'anthropic/claude-3.5-haiku', name: 'Claude 3.5 Haiku', category: 'general', costTier: 'low', supportsVision: true, description: 'Fast, 200K context' },
        { id: 'google/gemini-2.0-flash-001', name: 'Gemini 2.0 Flash', category: 'general', costTier: 'low', supportsVision: true, description: '1M context, ultra-fast' },
        { id: 'openai/gpt-4o-mini', name: 'GPT-4o Mini', category: 'fast', costTier: 'low', supportsVision: true, description: 'Budget-friendly speed' },
        { id: 'deepseek/deepseek-chat-v3-0324', name: 'DeepSeek V3', category: 'fast', costTier: 'low', description: 'Excellent value' },
        { id: 'meta-llama/llama-3.3-70b-instruct', name: 'Llama 3.3 70B', category: 'fast', costTier: 'low', description: 'Open source power' },
        { id: 'mistralai/mistral-large-2411', name: 'Mistral Large', category: 'fast', costTier: 'medium', description: 'EU compliance, 128K' },
        { id: 'anthropic/claude-opus-4', name: 'Claude Opus 4', category: 'creative', costTier: 'high', description: 'Best for writing' },
        { id: 'cohere/command-r-plus', name: 'Command R+', category: 'creative', costTier: 'medium', description: 'Great narratives' },
        { id: 'openai/o1', name: 'OpenAI o1', category: 'thinking', costTier: 'high', supportsThinking: true, description: 'Deep reasoning' },
        { id: 'openai/o1-mini', name: 'o1 Mini', category: 'thinking', costTier: 'medium', supportsThinking: true, description: 'Fast reasoning' },
        { id: 'openai/o3-mini', name: 'o3 Mini', category: 'thinking', costTier: 'medium', supportsThinking: true, description: 'Latest reasoning' },
        { id: 'deepseek/deepseek-r1', name: 'DeepSeek R1', category: 'thinking', costTier: 'low', supportsThinking: true, description: 'Open reasoning' },
        { id: 'google/gemini-2.5-pro', name: 'Gemini 2.5 Pro', category: 'thinking', costTier: 'medium', supportsThinking: true, description: '1M reasoning context' },
        { id: 'anthropic/claude-sonnet-4', name: 'Claude Sonnet 4', category: 'code', costTier: 'medium', description: 'Best coding model' },
        { id: 'deepseek/deepseek-coder', name: 'DeepSeek Coder', category: 'code', costTier: 'low', description: 'Code specialist' },
        { id: 'openai/gpt-4o', name: 'GPT-4o', category: 'code', costTier: 'medium', description: 'Strong coding' },
        { id: 'openai/gpt-4o', name: 'GPT-4o', category: 'vision', costTier: 'medium', supportsVision: true, description: 'Best image analysis' },
        { id: 'anthropic/claude-sonnet-4', name: 'Claude Sonnet 4', category: 'vision', costTier: 'medium', supportsVision: true, description: 'Excellent vision' },
        { id: 'google/gemini-2.5-pro', name: 'Gemini 2.5 Pro', category: 'vision', costTier: 'medium', supportsVision: true, description: 'Multi-image support' },
        { id: 'google/gemini-2.0-flash-001', name: 'Gemini 2.0 Flash', category: 'vision', costTier: 'low', supportsVision: true, description: 'Fast image analysis' },
        { id: 'openai/gpt-4o-mini', name: 'GPT-4o Mini', category: 'vision', costTier: 'low', supportsVision: true, description: 'Budget vision' },
    ], []);

    const getCostColor = (tier: CostTier) => {
        switch (tier) {
            case 'low': return 'text-green-500';
            case 'medium': return 'text-yellow-500';
            case 'high': return 'text-red-500';
            default: return 'text-muted-foreground';
        }
    };

    const getCostLabel = (tier: CostTier) => {
        switch (tier) {
            case 'low': return '$';
            case 'medium': return '$$';
            case 'high': return '$$$';
            default: return '';
        }
    };

    const getCategoryLabel = (category: ModelCategory) => {
        switch (category) {
            case 'popular': return 'Popular';
            case 'general': return 'General';
            case 'creative': return 'Creative';
            case 'fast': return 'Fast & Cheap';
            case 'reasoning': return 'Math & Reasoning';
            case 'code': return 'Code';
            case 'thinking': return 'Thinking Models';
            case 'vision': return 'Vision (Image Analysis)';
            default: return category;
        }
    };

    // Filter curated models based on availability
    const availableCuratedModels = useMemo(() => {
        if (availableModels.length === 0) return [];
        const availableIds = new Set(availableModels.map(m => m.id));
        return curatedModels.filter(m => availableIds.has(m.id));
    }, [availableModels, curatedModels]);

    // Group models by category
    const modelsByCategory = useMemo(() => {
        const groups: Record<ModelCategory, CuratedModel[]> = {
            popular: [], general: [], creative: [], fast: [], reasoning: [], code: [], thinking: [], vision: [],
        };
        availableCuratedModels.forEach(model => {
            if (groups[model.category]) groups[model.category].push(model);
        });
        return groups;
    }, [availableCuratedModels]);

    // Check if selected model supports thinking mode
    const selectedModelSupportsThinking = useMemo(() => {
        return curatedModels.find(m => m.id === selectedModel)?.supportsThinking || false;
    }, [selectedModel, curatedModels]);

    // Get popular/default models for quick selection
    const popularModels = useMemo(() => {
        return availableModels.length > 0
            ? availableModels.filter(m =>
                m.id.includes('gpt-4') || m.id.includes('claude-3') || m.id.includes('claude-3.5') ||
                m.id.includes('llama') || m.id.includes('gemini')
            ).slice(0, 20)
            : [
                {id: 'openai/gpt-4-turbo', name: 'GPT-4 Turbo'},
                {id: 'openai/gpt-3.5-turbo', name: 'GPT-3.5 Turbo'},
                {id: 'anthropic/claude-3.5-sonnet', name: 'Claude 3.5 Sonnet'},
                {id: 'perplexity/llama-3.1-sonar-large-128k-online', name: 'Perplexity Sonar'},
            ];
    }, [availableModels]);

    // Memoize selected model lookup for performance
    const selectedModelInfo = useMemo(() => {
        return availableModels.find(m => m.id === selectedModel) ||
            popularModels.find(m => m.id === selectedModel);
    }, [availableModels, popularModels, selectedModel]);

    // Get selected spaces - memoized
    const selectedSpaces = useMemo(() => {
        return spaces.filter(s => selectedSpaceIds.includes(s.id));
    }, [spaces, selectedSpaceIds]);

    // Get aggregated linked sources from all selected spaces - memoized
    const allLinkedSourceIds = useMemo(() => {
        return selectedSpaces.flatMap(space =>
            space.linkedSourceIds && Array.isArray(space.linkedSourceIds) ? space.linkedSourceIds : []
        );
    }, [selectedSpaces]);

    const linkedSources = useMemo(() => {
        return sources.filter(s => allLinkedSourceIds.includes(s.id));
    }, [sources, allLinkedSourceIds]);

    // Get directly selected sources (not from spaces) - memoized
    const directlySelectedSources = useMemo(() => {
        return sources.filter(s => selectedSourceIds.includes(s.id));
    }, [sources, selectedSourceIds]);

    // Combine all sources: space-linked + directly selected (avoid duplicates) - memoized
    const allSelectedSourceIds = useMemo(() => {
        return Array.from(new Set([
            ...allLinkedSourceIds,
            ...selectedSourceIds
        ]));
    }, [allLinkedSourceIds, selectedSourceIds]);

    const allSelectedSources = useMemo(() => {
        return sources.filter(s => allSelectedSourceIds.includes(s.id));
    }, [sources, allSelectedSourceIds]);

    const defaultAiTools = useMemo(() => [
        {id: "researcher", name: "Research Assistant", description: "Deep research and analysis"},
        {id: "writer", name: "Content Writer", description: "Create articles and documents"},
        {id: "analyst", name: "Data Analyst", description: "Analyze data and generate insights"},
        {id: "coder", name: "Code Assistant", description: "Help with programming tasks"},
        {id: "teacher", name: "Teaching Assistant", description: "Explain concepts clearly"},
    ], []);

    const aiTools = useMemo(() => [...defaultAiTools, ...customTools], [defaultAiTools, customTools]);

    // Helper function to get source icon
    const getSourceIcon = (type: string) => {
        switch (type) {
            case "upload":
                return <Upload className="h-4 w-4"/>;
            case "google-doc":
                return <ExternalLink className="h-4 w-4"/>;
            case "url":
                return <Link2 className="h-4 w-4"/>;
            default:
                return <FileText className="h-4 w-4"/>;
        }
    };

    const imageUploader = async (imgData: any) => {
        const form = new FormData();
        form.append('image', imgData);
        const apis: any = [
            '9d648e6865615783e0e40567df53a91a',
            '9469d0d5027b86ad6be1c0a5e8b14836',
            'f3f4bd051f8c51faa8027040e3cc82d8',
            '847928557f66be5d4f765e7423717cfc',
            'e4bd51cfe737c7500b329400a5a2f593',
            'd57863ac999c4eb3d82c8dbbf46abd9c',
            'a8ce334d48f8e7740b2f36425d5ff732',
            '6b9b3c27e85f28c883292be8e4eaed52',
            '74754f9c4a0d142d4f10eec548927e8a',
        ];
        const apiKey = apis[Math.floor(Math.random() * apis.length)];

        if (imgData) {
            try {
                setImgUploading(true);
                setIsStreaming(true);
                const url = 'https://api.imgbb.com/1/upload?key=' + apiKey;
                const rawResponse = await fetch(url, {
                    method: 'POST',
                    body: form
                });
                const res = await rawResponse.json();
                // console.log(res);
                setImgUploading(false);
                if (res['status'] === 200) {
                    const fileId = res['data']?.url?.match(/\/([\w-]+)\//)[1];
                    return fileId ? fileId : res['data'].id;
                } else {
                    toast({
                        title: "Image Upload Failed",
                        description: `Something went wrong, try again or try later...`,
                        variant: "destructive",
                    });
                }
            } catch (e) {
                toast({
                    title: "Failed to upload image",
                    description: typeof e === 'string' ? e : `Something went wrong, try again or try later...`,
                    variant: "destructive",
                });
            } finally {
                setIsStreaming(false);
                setImgUploading(false);
            }
        }
    }

    const streamServerDeepResearch = async function* (query: string) {
        const session = await getCurrentSession();
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        if (session?.access_token) {
            headers["Authorization"] = `Bearer ${session.access_token}`;
        }

        console.log(`[Deep Research Frontend] Starting Deep Research with model: perplexity/sonar-pro`);
        console.log(`[Deep Research Frontend] Query: ${query}`);

        const response = await fetch("/api/chat/deep-research", {
            method: "POST",
            headers,
            body: JSON.stringify({
                query,
                model: "perplexity/sonar-pro",
                threadId: currentThreadId || undefined
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`[Deep Research Frontend] ❌ Server error: ${response.statusText}`, errorText);
            throw new Error(`Server error: ${response.statusText}`);
        }

        console.log(`[Deep Research Frontend] ✅ Response OK (${response.status})`);

        const reader = response.body?.getReader();
        if (!reader) throw new Error("No response stream");

        const decoder = new TextDecoder();
        let buffer = "";

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    console.log(`[Deep Research Frontend] Stream completed`);
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop() || "";

                for (const line of lines) {
                    if (!line.startsWith("data: ")) continue;

                    try {
                        const event = JSON.parse(line.slice(6));
                        console.log(`[Deep Research Frontend] Event:`, event.type, event);
                        yield event;
                    } catch (e) {
                        console.error("Failed to parse SSE event:", e, "line:", line);
                    }
                }
            }
        } finally {
            reader.releaseLock();
            console.log(`[Deep Research Frontend] Reader released`);
        }
    };

    const handleDeepResearch = async () => {
        console.log(`[Deep Research Handler] ===== DEEP RESEARCH INITIATED =====`);
        console.log(`[Deep Research Handler] Deep Research Mode:`, deepResearchMode);

        const query = input.trim();
        if (!query) {
            console.log(`[Deep Research Handler] ❌ Query is empty, returning`);
            return;
        }

        console.log(`[Deep Research Handler] Query:`, query);
        console.log(`[Deep Research Handler] Current Thread ID:`, currentThreadId);

        const threadIdAtStart = currentThreadId;
        // Use topicId from URL ref as fallback if selectedTopicId is null (preserves topicId from URL navigation)
        const topicIdAtStart = selectedTopicId || (topicIdFromUrlRef.current ?? null);
        const spaceIdsAtStart = [...selectedSpaceIds];

        const userMessage: Message = {
            id: Date.now().toString(),
            role: "user",
            content: `[Deep Research] ${query}`,
            timestamp: new Date()
        };

        console.log(`[Deep Research Handler] Adding user message:`, userMessage.id);
        let pendingMessages = [...messages, userMessage];
        updateMessages([...messages, userMessage]);
        setInput("");
        if (textareaRef.current) textareaRef.current.style.height = '44px';
        setIsResearching(true);
        setResearchProgress(null);
        console.log(`[Deep Research Handler] Set isResearching to true`);

        const assistantId = (Date.now() + 1).toString();
        let assistantMessageCreated = false;
        let finalContent = "";
        let finalSources: any[] = [];
        let finalQueriesExecuted = 0;
        let finalSourcesFound = 0;

        try {
            console.log(`[Deep Research Handler] Starting research stream...`);
            for await (const progress of streamServerDeepResearch(query)) {
                console.log(`[Deep Research Handler] Received progress:`, progress.type, progress);
                setResearchProgress(progress);

                if (progress.type === "error") {
                    console.error(`[Deep Research Handler] ❌ Error from stream:`, progress.error);
                    throw new Error(progress.error || "Research failed");
                }

                if (progress.type === "delta" && progress.content) {
                    console.log(`[Deep Research Handler] Delta received: ${progress.content.length} chars`);
                    finalContent += progress.content;

                    if (!assistantMessageCreated) {
                        assistantMessageCreated = true;
                        console.log(`[Deep Research Handler] Creating assistant message`);
                        const assistantMessage: Message = {
                            id: assistantId,
                            role: "assistant",
                            content: "",
                            timestamp: new Date(),
                            model: "Deep Research",
                            autoSelected: false,
                        };
                        updateMessages((prev) => [...prev, assistantMessage]);
                        pendingMessages = [...pendingMessages, assistantMessage];
                    }

                    updateMessages((prev) =>
                        prev.map((msg) =>
                            msg.id === assistantId
                                ? { ...msg, content: finalContent }
                                : msg
                        )
                    );
                    pendingMessages = pendingMessages.map((msg) =>
                        msg.id === assistantId
                            ? { ...msg, content: finalContent }
                            : msg
                    );
                }

                if (progress.type === "complete" || progress.type === "done") {
                    console.log(`[Deep Research Handler] ✅ Research complete`);
                    // Capture final metrics from completion event
                    if (progress.queriesExecuted !== undefined) {
                        finalQueriesExecuted = progress.queriesExecuted;
                        console.log(`[Deep Research Handler] Final queries executed: ${finalQueriesExecuted}`);
                    }
                    if (progress.sourcesFound !== undefined) {
                        finalSourcesFound = progress.sourcesFound;
                        console.log(`[Deep Research Handler] Final sources found: ${finalSourcesFound}`);
                    }
                    break;
                }
            }

            console.log(`[Deep Research Handler] Research stream ended. Content length: ${finalContent.length}, Created: ${assistantMessageCreated}`);
            if (!assistantMessageCreated || !finalContent.trim()) {
                console.error(`[Deep Research Handler] ❌ No research results`);
                throw new Error("No research results received. Please try again.");
            }

            // Add fallback for metrics if they weren't captured from events
            if (finalQueriesExecuted === 0) {
                finalQueriesExecuted = 1;
                console.log(`[Deep Research Handler] Setting default queriesExecuted to 1`);
            }
            if (finalSourcesFound === 0) {
                // Try to count sources from response citations if available
                const citationMatch = finalContent.match(/\[\d+\]/g);
                finalSourcesFound = citationMatch ? citationMatch.length : 5;
                console.log(`[Deep Research Handler] Setting default sourcesFound to ${finalSourcesFound}`);
            }

            queryClient.invalidateQueries({ queryKey: ["/api/credits"] });

            const finalMessages = pendingMessages.map((m) => ({
                id: m.id,
                role: m.role,
                content: m.content,
                timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp,
                citations: m.citations ? [...m.citations] : undefined,
                contextCitations: m.contextCitations ? { ...m.contextCitations } : undefined,
                model: m.model,
                autoSelected: m.autoSelected,
            }));

            if (!threadIdAtStart) {
                const created = await createThreadMutation.mutateAsync({
                    userId: "",
                    messages: finalMessages,
                    linkedSpaceIds: spaceIdsAtStart,
                    attachedGitHubRepos: selectedGitHubRepos?.length ? selectedGitHubRepos : [],
                    contextMode,
                    chatMode,
                    agentId: selectedAgentId,
                    topicId: topicIdAtStart, // Use captured topicId to ensure it's linked correctly
                });
                const createdMessageCount = Array.isArray(created?.messages)
                    ? created.messages.length
                    : finalMessages.length;
                if (
                    created?.id &&
                    createdMessageCount >= 2 &&
                    threadNeedsAutoTitle(created.title)
                ) {
                    generateMetadataMutation.mutate(created.id);
                }
            } else {
                await updateThreadMutation.mutateAsync({
                    id: threadIdAtStart,
                    data: { messages: finalMessages },
                });

                if (finalMessages.length >= 2 && shouldAutoTitle(threadIdAtStart)) {
                    generateMetadataMutation.mutate(threadIdAtStart);
                }
            }

            console.log(`[Deep Research Handler] Showing toast with metrics: ${finalQueriesExecuted} queries, ${finalSourcesFound} sources`);
            toast({
                title: "Deep Research Complete",
                description: `Analyzed ${finalQueriesExecuted} queries and ${finalSourcesFound} sources`,
            });

        } catch (error) {
            console.error("Deep research error:", error);

            if (assistantMessageCreated) {
                updateMessages((prev) => prev.filter((msg) => msg.id !== assistantId));
            }

            toast({
                title: "Research Error",
                description: error instanceof Error ? error.message : "Deep research failed. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsResearching(false);
            setResearchProgress(null);
            setFileAttached([]);
        }
    };

    const { handleSend, handleContinue, handleRetry, isStreaming, setIsStreaming, streamingThinkingContent, pendingThreadStatus, pendingThreadLastError, retryPendingThreadSync, loadPendingDraft, listRecoverablePendingDrafts } = useChatSend({
        messages,
        updateMessages,
        input,
        setInput: setInputWithDraft,
        currentThreadId,
        setCurrentThreadId,
        workspaceId: currentWorkspaceId ?? null,
        spaceContextId: (() => {
            const params = new URLSearchParams(searchString || "");
            return params.get("spaceId") ?? activeChatSpaceId ?? null;
        })(),
        createThreadMutation,
        updateThreadMutation,
        generateMetadataMutation,
        shouldAutoTitle,
        isResearching,
        isRecording,
        onStopRecordingAndRequestSubmit: () => {
            shouldSubmitAfterRecordingRef.current = true;
            whisperVoiceRef.current?.stop?.();
        },
        deepResearchMode,
        onDeepResearch: handleDeepResearch,
        selectedSpaceIds,
        selectedTopicId,
        topicIdFromUrlRef, // Pass ref to preserve topicId from URL navigation
        spaceTopics,
        allTopics,
        isTopicsFetching,
        allSelectedSources,
        fileAttached,
        setFileAttached,
        selectedDocumentIds,
        usePersonalization,
        hasPersonalizationData,
        useSourceOfTruth: sourceOfTruthOn && hasSourceOfTruthCanon,
        userProfile: userProfile ?? null,
        selectedAgentId,
        selectedSuperPrompt,
        selectedWorkflow,
        selectedModel,
        setSelectedModel,
        modelInfo,
        setModelInfo,
        availableModels,
        isLoadingModels,
        isModelsFetchError,
        modelsFetchError,
        refetchModels,
        curatedModels,
        availableCuratedModels,
        autoSelectBest,
        thinkingMode,
        webSearchMode,
        selectedGitHubRepo,
        selectedGitHubRepos,
        cachedRepoContextRef,
        cachedRepoContext,
        cachedRepoContexts,
        isLoadingRepoContext,
        setServerPersonalization,
        user,
        completeMilestone: (id) => { completeMilestone(id as never); },
        hasMilestone: (id) => hasMilestone(id as never),
        contextMode,
        chatMode,
        useRAG,
        useFullScope,
    });

    const [streamingThinkingExpanded, setStreamingThinkingExpanded] = useState(false);
    const pendingRecoveryCheckedRef = useRef(false);

    useEffect(() => {
        if (!user?.id || homeMode || pendingRecoveryCheckedRef.current) return;
        pendingRecoveryCheckedRef.current = true;
        const drafts = listRecoverablePendingDrafts().filter(
            (d) => !isPendingDraftDismissed(user.id, d.draftId)
        );
        if (drafts.length === 0) return;

        // Prefer a recoverable draft for the open thread.
        const forCurrent = currentThreadId
            ? drafts.find(
                  (d) => d.threadId != null && String(d.threadId) === String(currentThreadId)
              )
            : undefined;
        if (forCurrent) {
            setPendingRecoveryDraft(forCurrent);
            return;
        }

        // Otherwise only prompt for brand-new unsaved chats (no threadId) —
        // never surface another thread's draft on every chat page.
        const orphan = drafts.find((d) => d.threadId == null);
        if (orphan) {
            setPendingRecoveryDraft(orphan);
        }
    }, [user?.id, homeMode, listRecoverablePendingDrafts, currentThreadId]);

    const handleRecoverPendingDraft = useCallback(() => {
        if (!pendingRecoveryDraft) return;
        loadPendingDraft(pendingRecoveryDraft);
        const restored = pendingRecoveryDraft.messages.map((m) => ({
            ...m,
            timestamp: new Date(m.timestamp),
        }));
        updateMessages(restored);
        if (pendingRecoveryDraft.threadId) {
            setCurrentThreadId(pendingRecoveryDraft.threadId);
        }
        setPendingRecoveryDraft(null);
        void retryPendingThreadSync();
    }, [pendingRecoveryDraft, loadPendingDraft, updateMessages, setCurrentThreadId, retryPendingThreadSync]);

    const handleDismissPendingRecovery = useCallback(() => {
        if (pendingRecoveryDraft && user?.id) {
            dismissPendingDraft(user.id, pendingRecoveryDraft.draftId);
        }
        setPendingRecoveryDraft(null);
    }, [pendingRecoveryDraft, user?.id]);

    useEffect(() => {
        if (isStreaming) setStreamingThinkingExpanded(false);
    }, [isStreaming]);

    // Latest assistant message — the voice conversation loop speaks it when streaming ends.
    const lastAssistantMessage = useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i--) {
            const m = messages[i];
            if (m?.role === "assistant") {
                return {
                    id: m.id,
                    content: typeof m.content === "string" ? m.content : "",
                    status: m.status,
                    errorMessage: m.errorMessage,
                };
            }
        }
        return null;
    }, [messages]);

    // (handleSend and isStreaming come from useChatSend above)

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            // Click the submit button instead of calling handleSend directly
            // This ensures the same code path is used as clicking the button
            if (submitButtonRef.current && !submitButtonRef.current.disabled) {
                submitButtonRef.current.click();
            }
        }
    }, []);

    const handleRenameTitle = useCallback((title: string) => {
        if (!currentThreadId) return;
        updateThreadMutation.mutate({
            id: currentThreadId,
            data: { title },
        });
    }, [currentThreadId, updateThreadMutation]);

    const handleClearContext = useCallback(() => {
        setSelectedSpaceIds([]);
        setSelectedTopicId(null);
        topicIdFromUrlRef.current = null; // Clear ref when context is cleared
        clearActiveChatContext();
    }, [setSelectedSpaceIds, setSelectedTopicId]);

    // Back: if we came from space Topics (have spaceId + topicId), go to that space with topic accordion expanded
    const handleBack = useCallback(() => {
        const params = new URLSearchParams(searchString || "");
        const urlSpaceId = params.get("spaceId");
        const urlTopicId = params.get("topicId");
        const spaceId = urlSpaceId || (thread?.linkedSpaceIds && thread.linkedSpaceIds[0]) || null;
        const topicId = urlTopicId || (thread?.topicId != null ? String(thread.topicId) : null) || null;
        if (spaceId) {
            const query = topicId ? `?tab=topics&expandTopic=${encodeURIComponent(topicId)}` : "";
            setWouterLocation(`/spaces/${spaceId}${query}`);
            return;
        }
        window.history.back();
    }, [searchString, thread?.linkedSpaceIds, thread?.topicId, setWouterLocation]);

    const hasAssistantMessages = useMemo(
        () => messages.some(m => m.role === "assistant"),
        [messages]
    );

    const handleFullscreenImage = useCallback((image: { url: string; alt: string }) => {
        setFullscreenImage(image);
    }, []);

    const handleDownloadImage = useCallback(async (imageUrl: string, filename: string) => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            toast({ title: "Image downloaded", description: "The image has been saved to your downloads." });
        } catch {
            window.open(imageUrl, '_blank');
        }
    }, [toast]);

    const handleActionPlan = useCallback(() => {
        setActionPlanDialogOpen(true);
    }, [setActionPlanDialogOpen]);

    const handleOpenSummarizeDialog = useCallback(() => {
        setSummarizeDialogOpen(true);
    }, [setSummarizeDialogOpen]);

    // Resize input textarea when content changes (typing or voice-to-text).
    // When input is cleared (e.g. after send), force default height so it doesn't stay expanded.
    useLayoutEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        if (!input || !input.trim()) {
            el.style.height = '44px';
            return;
        }
        el.style.height = Math.max(44, Math.min(el.scrollHeight, window.innerHeight * 0.3)) + 'px';
    }, [input]);

    // Optimized input handler - update state only; resize is handled by useLayoutEffect above
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const nextValue = e.target.value;
        inputRef.current = nextValue;
        if (inputDebounceRef.current) {
            window.clearTimeout(inputDebounceRef.current);
        }
        inputDebounceRef.current = window.setTimeout(() => {
            setInputWithDraft(nextValue);
        }, 120);
    }, [setInputWithDraft]);

    const handleTranscript = useCallback((finalText: string) => {
        if (submitAfterRecordingFallbackTimeoutRef.current) {
            window.clearTimeout(submitAfterRecordingFallbackTimeoutRef.current);
            submitAfterRecordingFallbackTimeoutRef.current = null;
        }

        if (!finalText.trim()) {
            return;
        }

        const initial = initialInputRef.current;
        const cleanedFinal = cleanSpeech(finalText);

        finalTranscriptRef.current = cleanedFinal;

        const targetInput = initial
            ? `${initial} ${cleanedFinal}`.trim()
            : cleanedFinal;

        inputRef.current = targetInput;
        setInput(targetInput);

        if (shouldSubmitAfterRecordingRef.current) {
            shouldSubmitAfterRecordingRef.current = false;
            handleSend(targetInput);
        }

        finalTranscriptRef.current = "";
    }, [cleanSpeech, handleSend]);

    const handleInterimTranscript = useCallback((text: string) => {
        if (text === "Listening..." || text === "Transcribing..." || !text.trim()) {
            return;
        }

        const initial = initialInputRef.current;
        const cleanedInterim = cleanSpeech(text);

        const combined = initial
            ? `${initial} ${cleanedInterim}`.trim()
            : cleanedInterim;

        inputRef.current = combined;
        setInput(combined);
    }, [cleanSpeech]);

    const handleRecordingChange = useCallback((recording: boolean) => {
        if (recording && !isRecording) {
            if (submitAfterRecordingFallbackTimeoutRef.current) {
                window.clearTimeout(submitAfterRecordingFallbackTimeoutRef.current);
                submitAfterRecordingFallbackTimeoutRef.current = null;
            }
            initialInputRef.current = input;
            finalTranscriptRef.current = "";
            shouldSubmitAfterRecordingRef.current = false;
        } else if (!recording) {
            if (shouldSubmitAfterRecordingRef.current) {
                if (submitAfterRecordingFallbackTimeoutRef.current) {
                    window.clearTimeout(submitAfterRecordingFallbackTimeoutRef.current);
                }
                submitAfterRecordingFallbackTimeoutRef.current = window.setTimeout(() => {
                    if (!shouldSubmitAfterRecordingRef.current) return;
                    const fallbackInput = inputRef.current.trim();
                    shouldSubmitAfterRecordingRef.current = false;
                    submitAfterRecordingFallbackTimeoutRef.current = null;
                    if (fallbackInput) {
                        handleSend(fallbackInput);
                    }
                }, 250);
            }
        }
        setIsRecording(recording);
    }, [handleSend, input, isRecording]);

    useEffect(() => {
        return () => {
            if (submitAfterRecordingFallbackTimeoutRef.current) {
                window.clearTimeout(submitAfterRecordingFallbackTimeoutRef.current);
            }
        };
    }, []);

    const handleRecordingTimeChange = useCallback((time: number) => {
        setRecordingTime(time);
    }, []);

    const handleEnhancedPrompt = useCallback((enhancedPrompt: string) => {
        inputRef.current = enhancedPrompt;
        setInput(enhancedPrompt);
    }, []);

    const handleSendEnhancedPrompt = useCallback((enhancedPrompt: string) => {
        inputRef.current = enhancedPrompt;
        setInput(enhancedPrompt);
        setTimeout(() => handleSend(enhancedPrompt), 100);
    }, [handleSend]);

    const handleCopy = async (content: string, id: string) => {
        try {
            await copyFormattedContent(content);
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
            toast({
                title: "Copied to clipboard",
                description: "The message has been copied to your clipboard.",
            });
        } catch (error) {
            try {
                await navigator.clipboard.writeText(content);
            } catch { /* ignore fallback failure */ }
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
            toast({
                title: "Copied to clipboard",
                description: "The message has been copied to your clipboard.",
            });
        }
    };

    const handleSendToSongCreator = useCallback(async () => {
        if (!thread || !currentThreadId) {
            toast({
                title: "No active thread",
                description: "Open a song production chat first.",
                variant: "destructive",
            });
            return;
        }
        if (!isSongProductionThread(thread.title)) {
            toast({
                title: "Not a song production thread",
                description: "Use a thread about songs, Suno, lyrics, or album production.",
                variant: "destructive",
            });
            return;
        }
        try {
            const { adminUrl } = await pushToSongCreator({
                threadId: currentThreadId,
                title: thread.title,
                messages: messages.map((m) => ({ role: m.role, content: m.content })),
            });
            window.open(adminUrl, "_blank", "noopener,noreferrer");
            toast({
                title: "Sent to Song Creator",
                description: "Opened Life Music admin. Import will load automatically.",
            });
        } catch (error) {
            toast({
                title: "Could not send to Song Creator",
                description: error instanceof Error ? error.message : "Unknown error",
                variant: "destructive",
            });
        }
    }, [thread, currentThreadId, messages, toast]);

    const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null);

    const handleDeleteMessage = async (messageId: string) => {
        if (!currentThreadId) return;

        if (!window.confirm("Are you sure you want to delete this message? This action cannot be undone.")) {
            return;
        }

        setDeletingMessageId(messageId);

        try {
            const updatedMessages = messages.filter((msg) => msg.id !== messageId);
            updateMessages(updatedMessages);

            const messagesForDb = updatedMessages.map(msg => ({
                id: msg.id,
                role: msg.role,
                content: msg.content,
                timestamp: msg.timestamp.toISOString(),
                citations: msg.citations,
                images: msg.images,
                model: msg.model,
                autoSelected: msg.autoSelected,
            }));

            await updateThreadMutation.mutateAsync({
                id: currentThreadId,
                data: { messages: messagesForDb },
            });

            toast({
                title: "Message deleted",
                description: "The message has been removed from this conversation.",
            });
        } catch (error) {
            queryClient.invalidateQueries({ queryKey: ['/api/chat-threads', currentThreadId] });
            toast({
                title: "Delete failed",
                description: "Could not delete the message. Please try again.",
                variant: "destructive",
            });
        } finally {
            setDeletingMessageId(null);
        }
    };

    /** Append a one-line history note to the thread (shown as an assistant message). */
    const appendThreadHistoryNote = useCallback(
        async (note: string) => {
            if (!currentThreadId) return;
            const noteMessage = {
                id: `note-${Date.now()}`,
                role: "assistant" as const,
                content: note,
                timestamp: new Date(),
                citations: undefined,
                images: undefined,
                model: undefined,
                autoSelected: undefined,
            };
            const presentMessages: NonNullable<(typeof messages)[number]>[] = [];
            for (const msg of messages) {
                if (msg) presentMessages.push(msg);
            }
            const updatedMessages = [...presentMessages, noteMessage];
            updateMessages(updatedMessages as unknown as Parameters<typeof updateMessages>[0]);

            const messagesForDb = updatedMessages.map((msg) => ({
                id: msg.id,
                role: msg.role,
                content: msg.content,
                timestamp: msg.timestamp.toISOString(),
                citations: msg.citations,
                images: msg.images,
                model: msg.model,
                autoSelected: msg.autoSelected,
            }));

            await updateThreadMutation.mutateAsync({
                id: currentThreadId,
                data: { messages: messagesForDb },
            });
        },
        [currentThreadId, messages, updateMessages, updateThreadMutation]
    );

    /** Phase 2 — run the selected agent on the latest AI response and save an action plan document. */
    const handleCreateActionPlan = useCallback(
        async (agentId: string) => {
            const lastAssistantMessage = [...messages].reverse().find((msg) => msg.role === "assistant");
            if (!currentThreadId || !lastAssistantMessage?.content?.trim()) {
                toast({
                    title: "Nothing to convert",
                    description: "There is no AI response in this thread yet.",
                    variant: "destructive",
                });
                return;
            }

            const gate = await creditsService.assertPositiveBalanceForAi();
            if (!gate.ok) {
                toast({
                    title: "Credits depleted",
                    description: "Purchase credits to continue using AI features.",
                    variant: "destructive",
                });
                setWouterLocation("/pricing");
                return;
            }

            try {
                const { raw } = await runThreadActionAgent({
                    agentId,
                    label: "Action plan",
                    instruction: ACTION_PLAN_INSTRUCTION,
                    content: lastAssistantMessage.content,
                });

                const threadTitle = thread?.title || "Untitled Chat";
                const title = deriveTitleFromMarkdown(raw, `Action Plan: ${threadTitle}`);
                const tagIds = await ensureTagIds(["action plan"]);

                const document = await knowledgeBaseService.createKnowledgeBaseItem(
                    {
                        title,
                        content: raw,
                        type: "note",
                        spaceId: selectedSpaceIds.length > 0 ? selectedSpaceIds[0] : null,
                        topicId: selectedTopicId,
                        tagIds,
                        metadata: {
                            sourceThreadId: currentThreadId,
                            sourceThreadTitle: threadTitle,
                            createdBy: `agent:${agentId}`,
                            actionPlanCreatedAt: new Date().toISOString(),
                        },
                    },
                    currentWorkspaceId ?? null
                );

                queryClient.invalidateQueries({ queryKey: ["/api/mastermind-documents"] });
                await appendThreadHistoryNote(`📋 Action plan document created: ${document.title}`);
                toast({ title: "Action plan created", description: document.title });
            } catch (error) {
                console.error("Action plan error:", error);
                toast({
                    title: "Action plan failed",
                    description:
                        error instanceof Error ? error.message : "Could not create the action plan.",
                    variant: "destructive",
                });
                throw error; // keep the dialog open so the user can retry (G4)
            }
        },
        [
            messages,
            currentThreadId,
            thread?.title,
            selectedSpaceIds,
            selectedTopicId,
            currentWorkspaceId,
            appendThreadHistoryNote,
            toast,
            setWouterLocation,
        ]
    );

    /** Phase 3 — run the selected agent for a markdown summary; optionally archive the thread. */
    const handleSummarizeArchive = useCallback(
        async ({ agentId, scope, archive }: SummarizeArchiveOptions) => {
            if (!currentThreadId || messages.length === 0) {
                toast({
                    title: "Cannot summarize",
                    description: "No conversation to summarize.",
                    variant: "destructive",
                });
                return;
            }

            const gate = await creditsService.assertPositiveBalanceForAi();
            if (!gate.ok) {
                toast({
                    title: "Credits depleted",
                    description: "Purchase credits to continue using AI features.",
                    variant: "destructive",
                });
                setWouterLocation("/pricing");
                return;
            }

            const lastAssistantMessage = [...messages].reverse().find((msg) => msg.role === "assistant");
            const content =
                scope === "last_response" && lastAssistantMessage?.content?.trim()
                    ? lastAssistantMessage.content
                    : formatThreadMessagesForSummary(messages);
            if (!content.trim()) {
                toast({
                    title: "Cannot summarize",
                    description: "There is no content to summarize yet.",
                    variant: "destructive",
                });
                return;
            }

            try {
                const threadTitle = thread?.title || "Untitled Chat";
                const { raw, parsed } = await runThreadActionAgent({
                    agentId,
                    label: "Summarize",
                    instruction: SUMMARIZE_INSTRUCTION,
                    content,
                });
                const summary = parseSummaryAgentResult(raw, parsed, threadTitle);
                const tagIds = await ensureTagIds(summary.tags);

                // DS EDIT 5 — workspace provenance: record which workspaces fed this
                // summary (current workspace/personal + the thread's RAG scope).
                const threadRagScope = await ragScopeService
                    .getThreadScope(currentThreadId)
                    .catch(() => null);
                const sourceWorkspaceIds = buildProvenanceWorkspaceIds(
                    currentWorkspaceId ?? null,
                    threadRagScope
                );

                const document = await knowledgeBaseService.createKnowledgeBaseItem(
                    {
                        title: summary.title,
                        content: summary.content,
                        type: archive ? "archive_summary" : "note",
                        spaceId: selectedSpaceIds.length > 0 ? selectedSpaceIds[0] : null,
                        topicId: selectedTopicId,
                        tagIds,
                        metadata: {
                            sourceThreadId: currentThreadId,
                            sourceThreadTitle: threadTitle,
                            createdBy: `agent:${agentId}`,
                            summarizeScope: scope,
                            sourceWorkspaceIds,
                            threadSummarySavedAt: new Date().toISOString(),
                        },
                    },
                    currentWorkspaceId ?? null
                );

                queryClient.invalidateQueries({ queryKey: ["/api/mastermind-documents"] });

                const scopeLabel = scope === "last_response" ? "Last response" : "Entire thread";
                const note = archive
                    ? `📄 ${scopeLabel} summarized to markdown: ${document.title} — Thread archived`
                    : `📄 ${scopeLabel} summarized to markdown: ${document.title}`;
                await appendThreadHistoryNote(note);

                if (archive) {
                    await chatThreadsService.markThreadArchived(currentThreadId, document.id);
                    queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
                    setCurrentThreadId(null);
                    updateMessages([]);
                }

                toast({
                    title: archive ? "Summarized & archived" : "Summary created",
                    description: document.title,
                });
            } catch (error) {
                console.error("Summarize & archive error:", error);
                toast({
                    title: "Summarize failed",
                    description:
                        error instanceof Error ? error.message : "Could not create the summary.",
                    variant: "destructive",
                });
                throw error; // keep the dialog open so the user can retry (G4)
            }
        },
        [
            currentThreadId,
            messages,
            thread?.title,
            selectedSpaceIds,
            selectedTopicId,
            currentWorkspaceId,
            appendThreadHistoryNote,
            setCurrentThreadId,
            updateMessages,
            toast,
            setWouterLocation,
        ]
    );

    /** Copy a deep link to the current thread. */
    const handleCopyThreadLink = useCallback(async () => {
        if (!currentThreadId) return;
        const url = `${window.location.origin}/chat?threadId=${encodeURIComponent(currentThreadId)}`;
        try {
            await navigator.clipboard.writeText(url);
            toast({ title: "Link copied", description: "A link to this thread is on your clipboard." });
        } catch {
            toast({
                title: "Failed to copy",
                description: "Could not copy the thread link.",
                variant: "destructive",
            });
        }
    }, [currentThreadId, toast]);

    const handleDeleteThread = useCallback(() => {
        if (!currentThreadId) return;
        setDeleteThreadConfirmOpen(true);
    }, [currentThreadId, setDeleteThreadConfirmOpen]);

    const handleConfirmDeleteThread = useCallback(async () => {
        if (!currentThreadId) return;
        setIsDeletingThread(true);
        try {
            await chatThreadsService.deleteChatThread(currentThreadId);
            queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
            setCurrentThreadId(null);
            updateMessages([]);
            setDeleteThreadConfirmOpen(false);
            toast({ title: "Thread deleted", description: "The conversation has been permanently deleted." });
        } catch (error) {
            console.error("Thread delete error:", error);
            toast({
                title: "Delete failed",
                description: error instanceof Error ? error.message : "Could not delete the thread.",
                variant: "destructive",
            });
        } finally {
            setIsDeletingThread(false);
        }
    }, [currentThreadId, setCurrentThreadId, updateMessages, toast, setDeleteThreadConfirmOpen, setIsDeletingThread]);

    const handleCreateDocumentFromSelection = (html: string, title: string) => {
        const sanitizedHtml = DOMPurify.sanitize(html);
        const topicTitle = getTopicTitleById(selectedTopicId ?? topicIdFromUrlRef.current ?? null);
        newDocCreatedAtRef.current = new Date();
        setNewDocContent(sanitizedHtml);
        setNewDocTitle(
            applyDocumentNamingPreferences(title, {
                topicTitle,
                createdAt: newDocCreatedAtRef.current,
                preferences: chatDocumentNamingPreferences,
            })
        );
        setNewDocSpaceId(selectedSpaceIds.length > 0 ? selectedSpaceIds[0] : null);
        setNewDocTopicId(selectedTopicId);
        setNewDocTagIds([]);
        setNewDocDialogOpen(true);
    };

    const handleClipToSidebar = (content: string) => {
        const sanitizedContent = DOMPurify.sanitize(content);
        clipToSidebar(sanitizedContent);
        toast({
            title: "Clipped to sidebar",
            description: "Selected content has been added to the document editor.",
        });
    };


    const handleSaveNewDocument = async () => {
        try {
            if (!user?.id) {
                throw new Error("You must be logged in to create a document");
            }

            const item = await knowledgeBaseService.createKnowledgeBaseItem({
                title: newDocTitle || "Untitled Document",
                content: newDocContent,
                type: "note",
                spaceId: newDocSpaceId,
                topicId: newDocTopicId,
                folderId: null,
                googleDocsId: null,
                tagIds: newDocTagIds,
                metadata: {},
            }, currentWorkspaceId ?? null);

            queryClient.invalidateQueries({ queryKey: ["knowledgebase"] });
            queryClient.invalidateQueries({ queryKey: ["/api/knowledgebase"] });

            setSelectedDocumentId(item.id);
            setIsCollapsed(false);

            setNewDocDialogOpen(false);
            setNewDocContent("");
            setNewDocTitle("");
            setNewDocSpaceId(null);
            setNewDocTopicId(null);
            setNewDocTagIds([]);

            toast({
                title: "Document created",
                description: currentThreadId
                    ? "Opened in the right sidebar for editing. It will be embedded for RAG search shortly. You can archive this chat from the thread menu (⋯) in the sidebar."
                    : "Opened in the right sidebar for editing. It will be embedded for RAG search shortly.",
            });
        } catch (error) {
            toast({
                title: "Failed to create document",
                description: error instanceof Error ? error.message : "Could not create document.",
                variant: "destructive",
            });
        }
    };

    const handleOpenSaveKnowledgeDialog = (content: string, messageId: string) => {
        const safeContent = content ?? "";
        // Convert markdown to HTML to preserve formatting when saving
        const htmlContent = renderMarkdownToHtml(safeContent);
        setSaveKnowledgeContent(htmlContent ?? "");
        setSaveKnowledgeMessageId(messageId);
        setNewDocSpaceId(selectedSpaceIds.length > 0 ? selectedSpaceIds[0] : null);
        setNewDocTopicId(selectedTopicId);
        setNewDocTagIds([]);
        setSaveKnowledgeTagSearch("");
        setIsSaveTagDropdownOpen(false);

        // Extract title from content - look for first heading or first line
        const lines = safeContent.split('\n').filter(line => (line ?? "").trim());
        let extractedTitle = "Untitled Document";

        // Look for markdown heading
        const headingMatch = safeContent.match(/^#+\s+(.+)/m);
        if (headingMatch && headingMatch[1] != null) {
            extractedTitle = String(headingMatch[1]).trim().substring(0, 100);
        } else if (lines.length > 0 && lines[0] != null) {
            // Use first meaningful line
            extractedTitle = String(lines[0]).replace(/^[#*\->\s]+/, '').trim().substring(0, 100);
        }

        const topicTitle = getTopicTitleById(selectedTopicId ?? topicIdFromUrlRef.current ?? null);
        saveKnowledgeCreatedAtRef.current = new Date();
        saveKnowledgeBaseTitleRef.current = extractedTitle;
        setSaveKnowledgeTitle(
            applyDocumentNamingPreferences(extractedTitle, {
                topicTitle,
                createdAt: saveKnowledgeCreatedAtRef.current,
                preferences: chatDocumentNamingPreferences,
            })
        );
        setSaveKnowledgeDialogOpen(true);
    };

    const handleOpenCitedDocument = useCallback(
        (documentId: string) => {
            setLibrarySidebarOpen(true);
            setSelectedDocumentId(documentId);
        },
        [setLibrarySidebarOpen, setSelectedDocumentId]
    );

    const renderMessage = useCallback((message: Message) => {
        const content = typeof message.content === 'string' ? message.content : '';
        const messageIndex = messages.findIndex((m) => m.id === message.id);
        const nextMessage = messageIndex >= 0 ? messages[messageIndex + 1] : undefined;
        const showRetry =
            message.role === "user" &&
            (!nextMessage ||
                nextMessage.role !== "assistant" ||
                nextMessage.status === "partial" ||
                nextMessage.status === "error");
        const rawEditBlock = hasAIEditBlock(content) ? parseAIEditBlock(content) : null;
        let editBlock = rawEditBlock;
        let editBlocked = false;

        if (rawEditBlock && message.role === 'assistant') {
            const turnCtx = getRepoEditTurnContext(message.id);
            if (turnCtx) {
                const validation = validateRepoEdit(rawEditBlock, {
                    fullFilePaths: turnCtx.fullFilePaths,
                    repoTreePaths: turnCtx.repoTreePaths,
                    attachedRepos: selectedGitHubRepos,
                });
                if (!validation.valid) {
                    editBlock = null;
                    editBlocked = true;
                    if (!editBlockedToastShownRef.current.has(message.id)) {
                        editBlockedToastShownRef.current.add(message.id);
                        toast({
                            title: "Edit blocked",
                            description: validation.reason ?? "Target file was not fully loaded for this turn.",
                            variant: "destructive",
                        });
                    }
                }
            }
        }

        const editGroups =
            editBlock && selectedGitHubRepos.length > 0
                ? groupEditsByRepo(editBlock.files, selectedGitHubRepos)
                : editBlock && selectedGitHubRepo
                  ? groupEditsByRepo(editBlock.files, [selectedGitHubRepo])
                  : new Map();

        const showDiff =
            message.role === 'assistant' &&
            selectedGitHubRepos.length > 0 &&
            editBlock &&
            editGroups.size > 0 &&
            !dismissedDiffPanels.has(message.id);

        const displayContent = (showDiff && editBlock) || editBlocked
            ? { ...message, content: stripEditBlock(content) }
            : message;

        return (
            <div key={message.id}>
                <MessageBubble
                    message={displayContent}
                    copiedId={copiedId}
                    savingToKnowledge={savingToKnowledge}
                    deletingMessageId={deletingMessageId}
                    speakingMessageId={speakingMessageId}
                    isSpeechPaused={isSpeechPaused}
                    availableVoices={availableVoices}
                    selectedVoiceUri={selectedVoiceUri}
                    speechRate={speechRate}
                    setSelectedVoiceUri={setSelectedVoiceUri}
                    setSpeechRate={setSpeechRate}
                    ttsVoices={ttsVoices}
                    selectedTtsVoiceId={selectedTtsVoiceId}
                    setSelectedTtsVoiceId={setSelectedTtsVoiceId}
                    ttsLoadingMessageId={ttsLoadingMessageId}
                    ttsErrorMessageId={ttsErrorMessageId}
                    ttsErrorMessage={ttsErrorMessage}
                    onCopy={handleCopy}
                    onSaveToKnowledge={handleOpenSaveKnowledgeDialog}
                    onDelete={handleDeleteMessage}
                    onFullscreenImage={handleFullscreenImage}
                    onDownloadImage={handleDownloadImage}
                    onActionPlan={handleActionPlan}
                    onSummarize={handleOpenSummarizeDialog}
                    speak={speak}
                    pause={pause}
                    resume={resume}
                    onOpenDocument={handleOpenCitedDocument}
                    onContinue={handleContinue}
                    onRetry={handleRetry}
                    showRetry={showRetry}
                    isStreaming={isStreaming}
                />
                {showDiff && editBlock && (
                    <div className="px-4 pb-2 space-y-2">
                        {[...editGroups.values()].map((group) => {
                            const panelKey = pushPanelKey(message.id, group.fullName);
                            const pushRecord = pushedRepoEdits[panelKey];
                            return (
                            <RepoDiffPanel
                                key={group.fullName}
                                editBlock={{ files: group.files, rawMatch: editBlock.rawMatch }}
                                owner={group.owner}
                                repo={group.name}
                                pushRecord={pushRecord}
                                defaultBranch={
                                    (cachedRepoContexts[group.fullName] as { info?: { default_branch?: string } } | undefined)
                                        ?.info?.default_branch
                                }
                                onPushed={(record) => {
                                    setPushedRepoEdits((prev) => {
                                        const next = { ...prev, [panelKey]: record };
                                        if (currentThreadId) {
                                            savePushedRepoEdit(currentThreadId, panelKey, record);
                                        }
                                        return next;
                                    });
                                }}
                                onRagFilesUpdated={(files) =>
                                    patchCachedRepoFileContent(group.fullName, files)
                                }
                                onDismiss={() =>
                                    setDismissedDiffPanels((prev) => new Set([...prev, message.id]))
                                }
                            />
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }, [
        messages,
        copiedId,
        savingToKnowledge,
        deletingMessageId,
        speakingMessageId,
        isSpeechPaused,
        availableVoices,
        selectedVoiceUri,
        speechRate,
        setSelectedVoiceUri,
        setSpeechRate,
        ttsVoices,
        selectedTtsVoiceId,
        setSelectedTtsVoiceId,
        ttsLoadingMessageId,
        ttsErrorMessageId,
        ttsErrorMessage,
        handleCopy,
        handleOpenSaveKnowledgeDialog,
        handleDeleteMessage,
        handleFullscreenImage,
        handleDownloadImage,
        handleActionPlan,
        handleOpenSummarizeDialog,
        speak,
        pause,
        resume,
        handleOpenCitedDocument,
        handleContinue,
        handleRetry,
        isStreaming,
        selectedGitHubRepo,
        selectedGitHubRepos,
        cachedRepoContexts,
        patchCachedRepoFileContent,
        pushedRepoEdits,
        currentThreadId,
        dismissedDiffPanels,
        setDismissedDiffPanels,
        toast,
    ]);

    const typingIndicator = useMemo(() => {
        if (!isStreaming) return null;
        const hasThinking = streamingThinkingContent?.trim().length > 0;
        return (
            <div className="flex justify-start">
                <div className="max-w-[80%] w-full">
                    <div className="rounded-lg p-4 bg-muted">
                        {hasThinking ? (
                            <Collapsible open={streamingThinkingExpanded} onOpenChange={setStreamingThinkingExpanded}>
                                <CollapsibleTrigger asChild>
                                    <button
                                        type="button"
                                        className="flex w-full min-w-0 items-center gap-2 rounded-md py-0.5 text-left outline-none transition-colors hover:bg-muted/80 focus-visible:ring-2 focus-visible:ring-ring"
                                        aria-expanded={streamingThinkingExpanded}
                                    >
                                        <span className="shrink-0 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            Thinking
                                        </span>
                                        {!streamingThinkingExpanded ? (
                                            <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground font-mono">
                                                {streamingThinkingContent}
                                            </span>
                                        ) : (
                                            <span className="min-w-0 flex-1 text-xs text-muted-foreground">
                                                Step-by-step reasoning (audit trail)
                                            </span>
                                        )}
                                        {streamingThinkingExpanded ? (
                                            <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
                                        ) : (
                                            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                                        )}
                                    </button>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <div className="mt-2 max-h-[40vh] overflow-y-auto border-t border-border/60 pt-2 text-sm text-muted-foreground whitespace-pre-wrap font-mono">
                                        {streamingThinkingContent}
                                    </div>
                                </CollapsibleContent>
                            </Collapsible>
                        ) : (
                            <div className="flex items-center gap-2">
                                <div className="flex gap-1">
                                    <div
                                        className="h-2 w-2 bg-muted-foreground rounded-full animate-bounce"
                                        style={{animationDelay: '0ms'}}></div>
                                    <div
                                        className="h-2 w-2 bg-muted-foreground rounded-full animate-bounce"
                                        style={{animationDelay: '150ms'}}></div>
                                    <div
                                        className="h-2 w-2 bg-muted-foreground rounded-full animate-bounce"
                                        style={{animationDelay: '300ms'}}></div>
                                </div>
                                <span className="text-sm text-muted-foreground">AI is thinking...</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }, [isStreaming, streamingThinkingContent, streamingThinkingExpanded]);

    const researchIndicator = useMemo(() => {
        if (!isResearching || !researchProgress) return null;
        return (
            <div className="flex justify-start">
                <div className="max-w-[80%] w-full">
                    <DeepResearchProgress
                        progress={researchProgress}
                        isActive={isResearching}
                    />
                </div>
            </div>
        );
    }, [isResearching, researchProgress]);

    const handleConfirmSaveToKnowledge = async () => {
        if (!saveKnowledgeMessageId) return;

        setSavingToKnowledge(saveKnowledgeMessageId);
        try {
            const title = (saveKnowledgeTitle ?? "").trim() || "Untitled Document";

            const item = await knowledgeBaseService.createKnowledgeBaseItem({
                title: title,
                content: saveKnowledgeContent ?? "",
                type: "note",
                spaceId: newDocSpaceId || null,
                topicId: newDocTopicId || null,
                tagIds: newDocTagIds || [],
                folderId: null,
                googleDocsId: null,
                videoUrl: null,
                metadata: {},
            }, currentWorkspaceId ?? null);

            queryClient.invalidateQueries({ queryKey: ["/api/knowledgebase"] });
            queryClient.invalidateQueries({ queryKey: ["knowledgebase"] });

            setSelectedDocumentId(item.id);
            setIsCollapsed(false);

            setSaveKnowledgeDialogOpen(false);
            setSaveKnowledgeContent("");
            setSaveKnowledgeMessageId(null);
            setNewDocSpaceId(null);
            setNewDocTopicId(null);
            setNewDocTagIds([]);

            toast({
                title: "Document saved",
                description: currentThreadId
                    ? "Opened in the right sidebar for editing. It will be embedded for RAG search shortly. You can archive this chat from the thread menu (⋯) in the sidebar."
                    : "Opened in the right sidebar for editing. It will be embedded for RAG search shortly.",
            });
        } catch (error) {
            toast({
                title: "Failed to save",
                description: error instanceof Error ? error.message : "Could not save message to knowledge base.",
                variant: "destructive",
            });
        } finally {
            setSavingToKnowledge(null);
        }
    };

    const handleCreateCustomTool = () => {
        if (!newToolName.trim()) {
            toast({
                title: "Name Required",
                description: "Please enter a name for your custom AI tool.",
                variant: "destructive",
            });
            return;
        }

        const newTool = {
            id: `custom-${Date.now()}`,
            name: newToolName,
            description: newToolDescription || "Custom AI tool",
        };

        setCustomTools([...customTools, newTool]);
        setNewToolName("");
        setNewToolDescription("");
        setCustomToolDialogOpen(false);
        toast({
            title: "Custom Tool Created",
            description: `${newToolName} has been added to your AI tools.`,
        });
    };

    return (
        <ChatWorkspaceErrorBoundary>
            <>
            <div className={`flex flex-col h-full min-h-0 w-full max-w-6xl mx-auto relative${className ? ` ${className}` : ""}`}>
                {/* Locked thread overlay - require PIN before showing content */}
                {currentThreadId && thread && (thread.isLocked === "true" || thread.isLocked === true) && !pinStatus?.isSessionValid && (
                    <div className="absolute inset-0 z-50 bg-background flex flex-col">
                        <LockedEntityOverlay
                            entityType="thread"
                            onUnlock={() => {
                                queryClient.invalidateQueries({ queryKey: ["/api/pin/status"] });
                            }}
                        />
                    </div>
                )}
                {/* Low Balance Warning */}
                {showLowBalanceWarning && (
                    <div className="flex items-center justify-between gap-2 px-3 py-1.5 mb-2 rounded-md border border-amber-500/50 bg-amber-500/10" data-testid="alert-low-credits">
                        <div className="flex items-center gap-2">
                            <Sparkles className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0"/>
                            <span className="text-xs">Low credit balance: <strong>{creditBalance} credits</strong> remaining</span>
                        </div>
                        <Link href="/settings" data-testid="link-upgrade-credits">
                            <Button size="sm" variant="outline" className="h-6 text-xs px-2">Upgrade Plan</Button>
                        </Link>
                    </div>
                )}

                <PendingThreadStatusBanner
                    status={pendingThreadStatus}
                    lastError={pendingThreadLastError}
                    onRetry={() => void retryPendingThreadSync()}
                    isRetrying={pendingThreadStatus === "syncing"}
                />

                {/* Compact Thread Header */}
                <ChatHeader
                    currentThread={thread}
                    currentThreadId={currentThreadId}
                    spaces={spaces}
                    spaceTopics={spaceTopics}
                    selectedSpaceIds={selectedSpaceIds}
                    selectedTopicId={selectedTopicId}
                    onClearContext={handleClearContext}
                    onBack={handleBack}
                    personalizationDetails={personalizationDetails}
                    showMetadataPanel={showMetadataPanel}
                    setShowMetadataPanel={setShowMetadataPanel}
                    threadLinkingDialogOpen={threadLinkingDialogOpen}
                    setThreadLinkingDialogOpen={setThreadLinkingDialogOpen}
                    isEditingTitle={isEditingTitle}
                    setIsEditingTitle={setIsEditingTitle}
                    editedTitle={editedTitle}
                    setEditedTitle={setEditedTitle}
                    onRenameTitle={handleRenameTitle}
                    onLockToggle={handleLockClick}
                    onShareThread={() => setShareThreadDialogOpen(true)}
                    onSendToSongCreator={
                        thread && isSongProductionThread(thread.title) && messages.length > 0
                            ? handleSendToSongCreator
                            : undefined
                    }
                    isLockPending={toggleLockMutation.isPending}
                    onCopyThreadLink={handleCopyThreadLink}
                    onSummarizeArchive={handleOpenSummarizeDialog}
                    onDeleteThread={handleDeleteThread}
                    hasMessages={messages.length > 0}
                    hasAssistantMessages={hasAssistantMessages}
                    pendingThreadStatus={pendingThreadStatus}
                    onUpdateTags={(tags) => {
                        if (currentThreadId) {
                            updateThreadMutation.mutate({
                                id: currentThreadId,
                                data: { tags }
                            });
                        }
                    }}
                    headerExtras={headerExtras}
                />
                {handoffSession && (
                    <div className="mb-2">
                        <AgentHandoffBanner
                            session={handoffSession}
                            agent={handoffAgent}
                            messages={messages}
                            currentThreadId={currentThreadId}
                            onDismiss={() => setHandoffSession(null)}
                        />
                    </div>
                )}
                {/* Messages Area */}
                <div className="relative flex-1 min-h-0 mb-4 overflow-hidden">
                    {messages.length === 0 ? (
                        <ChatEmptyState
                            chatGreeting={chatGreeting}
                            personalizedSuggestions={personalizedSuggestions}
                            suggestionsLoading={suggestionsLoading}
                            onSuggestionClick={(prompt) => setInput(prompt)}
                            minimal={minimalEmptyState}
                        />
                    ) : (
                        <ChatMessagesLegendList
                            renderMessage={renderMessage}
                            typingIndicator={typingIndicator}
                            researchIndicator={researchIndicator}
                            messagesEndRef={messagesEndRef}
                            listRef={messageListRef}
                            onNearEndChange={handleNearEndChange}
                        />
                    )}

                    {/* Scroll to bottom button — anchored to messages pane, not full chat column */}
                    {showScrollToBottom && messages.length > 0 && (
                        <Button
                            variant="secondary"
                            size="sm"
                            className="absolute bottom-4 left-1/2 -translate-x-1/2 shadow-lg z-10 gap-1"
                            onClick={scrollToBottom}
                            data-testid="button-scroll-to-bottom"
                        >
                            <ChevronDown className="h-4 w-4" />
                            Jump to bottom
                        </Button>
                    )}
                </div>

                <div className="border rounded-lg bg-background flex-shrink-0">
                    {/* DS EDIT 5aug26 — all composer icon controls consolidated onto the single action bar below the input */}
                    {voiceMode === "speech" ? (
                        <VoiceConversationPanel
                            active={voiceMode === "speech"}
                            isStreaming={isStreaming}
                            lastAssistantMessage={lastAssistantMessage}
                            onSendMessage={(text) => handleSend(text)}
                            onExit={() => setVoiceMode("off")}
                            onNotice={(message) => toast({ description: message })}
                            ttsVoiceId={chatPrefs.ttsVoiceId}
                            ttsSpeed={chatPrefs.ttsSpeed}
                        />
                    ) : (
                        <ChatInput
                            input={input}
                            onInputChange={handleInputChange}
                            onSend={handleSend}
                            onKeyDown={handleKeyDown}
                            isStreaming={isStreaming}
                            isLoadingModels={isLoadingModels}
                            voiceInputEnabled={voiceMode === "record"}
                            isRecording={isRecording}
                            recordingTime={recordingTime}
                            formatRecordingTime={formatRecordingTime}
                            textareaRef={textareaRef}
                            whisperVoiceRef={whisperVoiceRef}
                            submitButtonRef={submitButtonRef}
                            showPromptEnhancer={showPromptEnhancer}
                            setShowPromptEnhancer={setShowPromptEnhancer}
                            onTranscript={handleTranscript}
                            onInterimTranscript={handleInterimTranscript}
                            onRecordingChange={handleRecordingChange}
                            onRecordingTimeChange={handleRecordingTimeChange}
                            onEnhancedPrompt={handleEnhancedPrompt}
                            onSendEnhancedPrompt={handleSendEnhancedPrompt}
                        />
                    )}

                    {/* Compact Action Bar - Icon-only with tooltips (DS EDIT 5aug26: one row for ALL composer controls) */}
                    <div className="flex items-center justify-between gap-2 px-2 pb-2 flex-wrap">
                        <div className="flex items-center gap-1 flex-wrap">
                            <VoiceModeToggle mode={voiceMode} onModeChange={setVoiceMode} disabled={isStreaming} />
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant={usePersonalization ? "default" : "ghost"}
                                        size="icon"
                                        onClick={() => setUsePersonalization(!usePersonalization)}
                                        disabled={!hasPersonalizationData}
                                        className={`h-8 w-8 ${usePersonalization ? "shadow-[0_0_8px_hsl(var(--primary)/0.5)]" : ""}`}
                                        data-testid="button-personalization-bar"
                                        aria-label={usePersonalization ? "Personalization active" : "Personalization off"}
                                    >
                                        <UserCog className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                    <p>{usePersonalization ? "Personalization active" : "Personalization off"}</p>
                                    {!hasPersonalizationData && (
                                        <p className="text-xs text-muted-foreground">Add profile data in Settings to enable</p>
                                    )}
                                </TooltipContent>
                            </Tooltip>
                            <UnifiedAttachmentPanel
                                sources={sources}
                                selectedSourceIds={selectedSourceIds}
                                onSourcesChange={setSelectedSourceIds}
                                selectedDocumentIds={selectedDocumentIds}
                                onDocumentsChange={setSelectedDocumentIds}
                                fileAttached={fileAttached}
                                onFilesChange={setFileAttached}
                                getContentType={getContentType}
                                selectedGitHubRepo={selectedGitHubRepo}
                                onGitHubRepoChange={setSelectedGitHubRepo}
                                selectedGitHubRepos={selectedGitHubRepos}
                                onToggleGitHubRepo={toggleGitHubRepo}
                                maxAttachedRepos={maxAttachedRepos}
                                repoIndexingProgress={repoIndexingProgress}
                                repoIndexingProgressMap={repoIndexingProgressMap}
                                onRefreshRepoIndex={refreshRepoIndexForSelection}
                                persistenceContext={{
                                    threadId: currentThreadId,
                                    spaceId: selectedSpaceIds.length > 0 ? selectedSpaceIds[0] : null,
                                    topicId: selectedTopicId,
                                    workspaceId: currentWorkspaceId ?? null,
                                }}
                                onSourcesPersisted={(sourceIds) => {
                                    setSelectedSourceIds(prev => [...new Set([...prev, ...sourceIds])]);
                                    queryClient.invalidateQueries({ queryKey: ['/api/sources'] });
                                }}
                            />
                            <ScreenCaptureButton
                                onCapture={(dataUrl, meta) => {
                                    setFileAttached((prev) => [
                                        ...prev,
                                        { type: "image", data: dataUrl, filename: "screenshot.png", meta },
                                    ]);
                                    const visionModel = curatedModels.find((m) => m.category === "vision" && m.supportsVision);
                                    if (visionModel && !curatedModels.find((m) => m.id === selectedModel)?.supportsVision) {
                                        setSelectedModel(visionModel.id);
                                    }
                                }}
                                onCaptureText={(text) => setInput(text)}
                                onSuggestPrompt={(prompt) => setInput((prev) => prev.trim() ? prev : prompt)}
                                countdownSeconds={2}
                                disabled={isStreaming}
                            />
                            <ToolsPopover
                                toolsMenuOpen={toolsMenuOpen}
                                setToolsMenuOpen={setToolsMenuOpen}
                                modelSelectorOpen={modelSelectorOpen}
                                setModelSelectorOpen={setModelSelectorOpen}
                                selectedModel={selectedModel}
                                setSelectedModel={setSelectedModel}
                                setModelInfo={setModelInfo}
                                availableModels={availableModels}
                                modelsLastUpdated={modelsLastUpdated}
                                refetchModels={refetchModels}
                                isRefreshingModels={isRefreshingModels}
                                isLoadingModels={isLoadingModels}
                                curatedModels={curatedModels}
                                modelsByCategory={modelsByCategory}
                                selectedModelInfo={selectedModelInfo}
                                selectedModelSupportsThinking={selectedModelSupportsThinking}
                                getCostColor={getCostColor}
                                getCostLabel={getCostLabel}
                                autoSelectBest={autoSelectBest}
                                setAutoSelectBest={setAutoSelectBest}
                                thinkingMode={thinkingMode}
                                setThinkingMode={setThinkingMode}
                                selectedSuperPrompt={selectedSuperPrompt}
                                setSelectedSuperPrompt={setSelectedSuperPromptAndDefault}
                                selectedAgentId={selectedAgentId}
                                setSelectedAgentId={setSelectedAgentIdAndDefault}
                                selectedWorkflow={selectedWorkflow}
                                setSelectedWorkflow={setSelectedWorkflow}
                                deepResearchMode={deepResearchMode}
                                setDeepResearchMode={setDeepResearchMode}
                                webSearchMode={webSearchMode}
                                setWebSearchMode={setWebSearchMode}
                                chatMode={chatMode}
                                setChatMode={setChatModeAndDefault}
                                contextMode={contextMode}
                                setContextMode={setContextModeAndDefault}
                                allSelectedSources={allSelectedSources}
                                showPromptEnhancer={showPromptEnhancer}
                                setShowPromptEnhancer={setShowPromptEnhancer}
                                canEnhancePrompt={!!input.trim()}
                                hasPersonalizationData={hasPersonalizationData}
                                usePersonalization={usePersonalization}
                                setUsePersonalization={setUsePersonalization}
                                hasSourceOfTruth={hasSourceOfTruthCanon}
                                useSourceOfTruth={sourceOfTruthOn}
                                setUseSourceOfTruth={setSourceOfTruthOn}
                                useRAG={useRAG}
                                setUseRAG={setUseRAG}
                                selectedSpaceIds={selectedSpaceIds}
                                selectedTopicId={selectedTopicId}
                                setSelectedTopicId={setSelectedTopicId}
                                useFullScope={useFullScope}
                                setUseFullScope={setUseFullScope}
                                spaceTopics={spaceTopics.map((t) => ({ id: t.id, name: t.name }))}
                                currentThreadId={currentThreadId}
                                onOpenAdvanced={() => setAdvancedSettingsOpen(true)}
                            />
                            {selectedGitHubRepos.length > 0 && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                            onClick={() => setRepoExplorerOpen(true)}
                                            data-testid="button-repo-file-explorer"
                                            aria-label="Browse repository files"
                                        >
                                            <FolderTree className="h-4 w-4" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">Browse attached repository files</TooltipContent>
                                </Tooltip>
                            )}
                            {selectedGitHubRepos.some(
                                (r) => (repoIndexingProgressMap?.[r.fullName] ?? repoIndexingProgress)?.status === 'indexing'
                            ) && (
                                <div className="flex items-center gap-2 min-w-0 shrink-0">
                                    {selectedGitHubRepos
                                        .filter((r) => (repoIndexingProgressMap?.[r.fullName] ?? repoIndexingProgress)?.status === 'indexing')
                                        .map((r) => {
                                            const prog = repoIndexingProgressMap?.[r.fullName] ?? repoIndexingProgress;
                                            if (!prog || prog.status !== 'indexing') return null;
                                            const isStale = prog.lastHeartbeat
                                                ? Date.now() - new Date(prog.lastHeartbeat).getTime() > 60_000
                                                : false;
                                            return (
                                                <div key={r.fullName} className="flex-1 min-w-0 flex flex-col gap-0.5 max-w-[160px]">
                                                    <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                                                        <span className="truncate" title={r.fullName}>
                                                            {r.name}
                                                            {prog.indexStatus === 'pass1Only' ? ' (pass1)' : ''}
                                                            {isStale ? ' ⚠' : ''}
                                                        </span>
                                                        <span className="shrink-0 ml-1">{prog.progress}%</span>
                                                    </div>
                                                    <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
                                                        <div
                                                            className="h-full bg-primary transition-all duration-300"
                                                            style={{ width: `${Math.min(100, prog.progress)}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            )}
                        </div>

                        {/* Right side - Active mode badges + Model indicator */}
                        <div className="flex items-center gap-1.5">
                            {sourceOfTruthOn && hasSourceOfTruthCanon && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Badge
                                            variant="secondary"
                                            className="text-[10px] gap-0.5 h-5 px-1.5 cursor-pointer hover:bg-primary/20"
                                            onClick={() => setSourceOfTruthOn(false)}
                                            title="Source of Truth canon is included in context. Click to turn off."
                                        >
                                            <Shield className="h-2.5 w-2.5"/>
                                            Canon
                                        </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">Your Source of Truth documents are in context. Click to turn off.</TooltipContent>
                                </Tooltip>
                            )}
                            {useRAG && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Badge
                                            variant="secondary"
                                            className="text-[10px] gap-0.5 h-5 px-1.5 cursor-pointer hover:bg-primary/20"
                                            onClick={() => setUseRAG(false)}
                                            title="Semantic search from your knowledge base is on. Click to turn off."
                                        >
                                            <Brain className="h-2.5 w-2.5"/>
                                            RAG
                                        </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">Semantic search from your knowledge base. Click to turn off.</TooltipContent>
                                </Tooltip>
                            )}
                            {!useRAG && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Badge
                                            variant="outline"
                                            className="text-[10px] gap-0.5 h-5 px-1.5 cursor-pointer opacity-70 hover:opacity-100"
                                            onClick={() => setUseRAG(true)}
                                            title="Turn on RAG to search your documents by meaning."
                                        >
                                            RAG off
                                        </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">Click to turn on semantic search from your knowledge base.</TooltipContent>
                                </Tooltip>
                            )}
                            {webSearchMode === "open" && (
                                <Badge variant="secondary" className="text-[10px] gap-0.5 h-5 px-1.5">
                                    <Globe className="h-2.5 w-2.5"/>
                                    Web
                                </Badge>
                            )}
                            {deepResearchMode && (
                                <Badge variant="secondary" className="text-[10px] gap-0.5 h-5 px-1.5">
                                    <Zap className="h-2.5 w-2.5"/>
                                    Research
                                </Badge>
                            )}
                            {chatMode !== "normal" && (
                                <Badge variant="secondary" className="text-[10px] gap-0.5 h-5 px-1.5">
                                    {chatMode === "questioning" ? <HelpCircle className="h-2.5 w-2.5"/> : <MessagesSquare className="h-2.5 w-2.5"/>}
                                    {chatMode === "questioning" ? "Q" : "D"}
                                </Badge>
                            )}
                            {selectedAgentId && (
                                <Badge variant="secondary" className="text-[10px] gap-0.5 h-5 px-1.5">
                                    <Bot className="h-2.5 w-2.5"/>
                                </Badge>
                            )}
                            {selectedSuperPrompt && (
                                <Badge
                                    variant="secondary"
                                    className="text-[10px] gap-0.5 h-5 px-1.5 cursor-pointer hover:bg-destructive/20"
                                    onClick={() => setSelectedSuperPrompt(null)}
                                    title={selectedSuperPrompt.name}
                                >
                                    <Wand2 className="h-2.5 w-2.5"/>
                                    <span className="truncate max-w-[60px]">{selectedSuperPrompt.name}</span>
                                    <X className="h-2.5 w-2.5"/>
                                </Badge>
                            )}
                            {fileAttached.length > 0 && (
                                <Badge variant="secondary" className="text-[10px] gap-0.5 h-5 px-1.5 cursor-pointer hover:bg-destructive/20" onClick={() => setFileAttached([])}>
                                    <Paperclip className="h-2.5 w-2.5"/>
                                    {fileAttached.length}
                                    <X className="h-2.5 w-2.5"/>
                                </Badge>
                            )}
                            <span className="text-[10px] text-muted-foreground hidden sm:inline truncate max-w-[80px]">
                                {selectedModelInfo?.name?.split(' ')[0] || selectedModel.split('/').pop()?.split('-')[0]}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Advanced chat settings drawer */}
            <AdvancedSettingsPanel
                open={advancedSettingsOpen}
                onOpenChange={setAdvancedSettingsOpen}
                availableModels={availableModels}
                selectedAgentId={selectedAgentId}
                setSelectedAgentId={setSelectedAgentIdAndDefault}
                hasPersonalizationData={hasPersonalizationData}
                showPromptEnhancer={showPromptEnhancer}
                setShowPromptEnhancer={setShowPromptEnhancer}
                canEnhancePrompt={!!input.trim()}
            />

            {/* Repo file explorer drawer (DS Coder) */}
            <Sheet open={repoExplorerOpen} onOpenChange={setRepoExplorerOpen}>
                <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
                    <SheetHeader>
                        <SheetTitle className="flex items-center gap-2 text-sm">
                            <FolderTree className="h-4 w-4" />
                            Repository Files
                        </SheetTitle>
                    </SheetHeader>
                    <div className="min-h-0 flex-1 pt-2">
                        <RepoFileExplorer
                            repos={selectedGitHubRepos}
                            onInsertReferences={(items) => {
                                const refs = items
                                    .map(({ path, repo }) => `${repo.fullName}: ${path}`)
                                    .join(" ");
                                setInput((prev) =>
                                    prev.trim() ? `${prev.trimEnd()} ${refs} ` : `${refs} `
                                );
                                textareaRef.current?.focus();
                            }}
                        />
                    </div>
                </SheetContent>
            </Sheet>

            {/* Source Selector Dialog */}
            <Dialog open={sourceDialogOpen} onOpenChange={setSourceDialogOpen}>
                {sourceDialogOpen && (
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>Select Reference Sources</DialogTitle>
                            <DialogDescription>
                                Choose sources to provide context for the AI.
                            </DialogDescription>
                        </DialogHeader>
                        <ScrollArea className="max-h-[400px] mt-4">
                            <div className="space-y-2">
                                {sources.length === 0 ? (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <FileText className="h-12 w-12 mx-auto mb-2 opacity-20"/>
                                        <p className="text-sm">No sources available</p>
                                    </div>
                                ) : (
                                    sources.map((source) => (
                                        <div
                                            key={source.id}
                                            className={`p-3 rounded-lg border transition-colors hover-elevate ${
                                                selectedSourceIds.includes(source.id) ? "border-primary bg-primary/5" : ""
                                            }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <Checkbox
                                                    checked={selectedSourceIds.includes(source.id)}
                                                    onCheckedChange={(checked) => {
                                                        if (checked) {
                                                            setSelectedSourceIds([...selectedSourceIds, source.id]);
                                                        } else {
                                                            setSelectedSourceIds(selectedSourceIds.filter(id => id !== source.id));
                                                        }
                                                    }}
                                                />
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        {getSourceIcon(source.type)}
                                                        <span className="font-medium">{source.title}</span>
                                                        <Badge variant="outline" className="text-xs capitalize">{source.type}</Badge>
                                                    </div>
                                                    {source.content && (
                                                        <div className="text-sm text-muted-foreground line-clamp-2">{source.content}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </ScrollArea>
                        <div className="mt-4 flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">
                                {selectedSourceIds.length} selected
                            </span>
                            <div className="flex gap-2">
                                {selectedSourceIds.length > 0 && (
                                    <Button variant="outline" size="sm" onClick={() => setSelectedSourceIds([])}>
                                        Clear
                                    </Button>
                                )}
                                <Button size="sm" onClick={() => setSourceDialogOpen(false)}>Done</Button>
                            </div>
                        </div>
                    </DialogContent>
                )}
            </Dialog>

            {/* Space Creation Wizard */}
            {spaceWizardOpen && (
                <SpaceWizard
                    open={spaceWizardOpen}
                    onOpenChange={setSpaceWizardOpen}
                    sources={sources}
                />
            )}

            {/* Custom Tool Creation Dialog */}
            <Dialog open={customToolDialogOpen} onOpenChange={setCustomToolDialogOpen}>
                {customToolDialogOpen && (
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Create Custom AI Tool</DialogTitle>
                            <DialogDescription>
                                Define a specialized AI agent with a custom name and purpose
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                            <div>
                                <label className="text-sm font-medium mb-1.5 block">Tool Name</label>
                                <Input
                                    placeholder="e.g., Marketing Expert"
                                    value={newToolName}
                                    onChange={(e) => setNewToolName(e.target.value)}
                                    data-testid="input-custom-tool-name"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1.5 block">Description</label>
                                <Textarea
                                    placeholder="What does this AI tool specialize in?"
                                    value={newToolDescription}
                                    onChange={(e) => setNewToolDescription(e.target.value)}
                                    className="min-h-[80px]"
                                    data-testid="input-custom-tool-description"
                                />
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setCustomToolDialogOpen(false);
                                    setNewToolName("");
                                    setNewToolDescription("");
                                }}
                                data-testid="button-cancel-custom-tool"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleCreateCustomTool}
                                data-testid="button-save-custom-tool"
                            >
                                <Plus className="h-4 w-4 mr-1"/>
                                Create Tool
                            </Button>
                        </div>
                    </DialogContent>
                )}
            </Dialog>

            {/* New Document Dialog */}
            <Dialog open={newDocDialogOpen} onOpenChange={setNewDocDialogOpen}>
                {newDocDialogOpen && (
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Create New Document</DialogTitle>
                        <DialogDescription>
                            Save the selected text as a new document and link it to your knowledge base
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label htmlFor="doc-title">Title</Label>
                            <Input
                                id="doc-title"
                                placeholder="Document title..."
                                value={newDocTitle}
                                onChange={(e) => {
                                    onUserEditedTitle();
                                    setNewDocTitle(e.target.value);
                                }}
                                data-testid="input-new-doc-title"
                            />
                        </div>
                        <div>
                            <Label htmlFor="doc-space">Link to Space (Optional)</Label>
                            <select
                                id="doc-space"
                                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                                value={newDocSpaceId || ""}
                                onChange={(e) => {
                                    setNewDocSpaceId(e.target.value || null);
                                    setNewDocTopicId(null);
                                }}
                                data-testid="select-new-doc-space"
                            >
                                <option value="">No space</option>
                                {spaces.map((space) => (
                                    <option key={space.id} value={space.id}>{space.name}</option>
                                ))}
                            </select>
                        </div>
                        {newDocSpaceId && (
                            <div>
                                <Label htmlFor="doc-topic">Link to Topic (Optional)</Label>
                                <select
                                    id="doc-topic"
                                    className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                                    value={newDocTopicId || ""}
                                    onChange={(e) => setNewDocTopicId(e.target.value || null)}
                                    data-testid="select-new-doc-topic"
                                >
                                    <option value="">No topic</option>
                                    {(Array.isArray(allTopics) ? allTopics : []).filter(t => t.spaceId === newDocSpaceId).map((topic) => (
                                        <option key={topic.id} value={topic.id}>{topic.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div>
                            <Label>Tags (Optional)</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {allTags.map((tag) => (
                                    <Badge
                                        key={tag.id}
                                        variant={newDocTagIds.includes(tag.id) ? "default" : "outline"}
                                        className="cursor-pointer hover-elevate"
                                        onClick={() => {
                                            setNewDocTagIds(prev =>
                                                prev.includes(tag.id)
                                                    ? prev.filter(id => id !== tag.id)
                                                    : [...prev, tag.id]
                                            );
                                        }}
                                        data-testid={`tag-${tag.id}`}
                                    >
                                        {tag.name}
                                    </Badge>
                                ))}
                                {allTags.length === 0 && (
                                    <span className="text-sm text-muted-foreground">No tags available</span>
                                )}
                            </div>
                        </div>
                        <div>
                            <Label>Content Preview</Label>
                            <div className="mt-2 p-3 bg-muted rounded-md text-sm max-h-32 overflow-auto">
                                {newDocContent.substring(0, 300)}
                                {newDocContent.length > 300 && "..."}
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="mt-6">
                        <Button
                            variant="outline"
                            onClick={() => setNewDocDialogOpen(false)}
                            data-testid="button-cancel-new-doc"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveNewDocument}
                            data-testid="button-save-new-doc"
                        >
                            <FilePlus className="h-4 w-4 mr-1"/>
                            Create Document
                        </Button>
                    </DialogFooter>
                </DialogContent>
                )}
            </Dialog>

            {/* Save as Doc Dialog */}
            <Dialog open={saveKnowledgeDialogOpen} onOpenChange={setSaveKnowledgeDialogOpen}>
                {saveKnowledgeDialogOpen && (
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Save as Document</DialogTitle>
                        <DialogDescription>
                            Choose where to organize this content in your knowledge base
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label htmlFor="save-space">Link to Space (Optional)</Label>
                            <select
                                id="save-space"
                                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                                value={newDocSpaceId || ""}
                                onChange={(e) => {
                                    setNewDocSpaceId(e.target.value || null);
                                    setNewDocTopicId(null);
                                }}
                                data-testid="select-save-knowledge-space"
                            >
                                <option value="">No space</option>
                                {spaces.map((space) => (
                                    <option key={space.id} value={space.id}>{space.name}</option>
                                ))}
                            </select>
                        </div>
                        {newDocSpaceId && (
                            <div>
                                <Label htmlFor="save-topic">Link to Topic (Optional)</Label>
                                <select
                                    id="save-topic"
                                    className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                                    value={newDocTopicId || ""}
                                    onChange={(e) => setNewDocTopicId(e.target.value || null)}
                                    data-testid="select-save-knowledge-topic"
                                >
                                    <option value="">No topic</option>
                                    {(Array.isArray(allTopics) ? allTopics : []).filter(t => t.spaceId === newDocSpaceId).map((topic) => (
                                        <option key={topic.id} value={topic.id}>{topic.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div>
                            <Label>Tags (Optional)</Label>
                            {/* Selected tags display */}
                            {newDocTagIds.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2 mb-2">
                                    {newDocTagIds.map((tagId) => {
                                        const tag = allTags.find(t => t.id === tagId);
                                        return tag ? (
                                            <Badge
                                                key={tag.id}
                                                variant="secondary"
                                                className="gap-1 pr-1 text-xs"
                                                data-testid={`selected-tag-${tag.id}`}
                                            >
                                                <Tags className="h-3 w-3" />
                                                {tag.name}
                                                <button
                                                    onClick={() => setNewDocTagIds(prev => prev.filter(id => id !== tag.id))}
                                                    className="ml-1 rounded-full hover-elevate p-0.5"
                                                    data-testid={`button-remove-tag-${tag.id}`}
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </Badge>
                                        ) : null;
                                    })}
                                </div>
                            )}
                            {/* Searchable tag dropdown */}
                            <Popover open={isSaveTagDropdownOpen} onOpenChange={setIsSaveTagDropdownOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={isSaveTagDropdownOpen}
                                        className="w-full justify-between h-9 font-normal"
                                        data-testid="button-open-tag-dropdown"
                                    >
                                        <span className="flex items-center gap-2 text-muted-foreground">
                                            <Tags className="h-4 w-4" />
                                            {newDocTagIds.length > 0 ? `${newDocTagIds.length} tag(s) selected` : "Search or create tags..."}
                                        </span>
                                        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[300px] p-0" align="start">
                                    <Command>
                                        <CommandInput
                                            placeholder="Search tags..."
                                            value={saveKnowledgeTagSearch}
                                            onValueChange={setSaveKnowledgeTagSearch}
                                            data-testid="input-tag-search"
                                        />
                                        <CommandList>
                                            <CommandEmpty>
                                                {(saveKnowledgeTagSearch ?? "").trim() ? (
                                                    <button
                                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm hover-elevate"
                                                        onClick={async () => {
                                                            try {
                                                                const newTag = await tagsService.createTag({ name: (saveKnowledgeTagSearch ?? "").trim() });
                                                                queryClient.invalidateQueries({ queryKey: ["/api/tags"] });
                                                                setNewDocTagIds(prev => [...prev, newTag.id]);
                                                                setSaveKnowledgeTagSearch("");
                                                                toast({ title: "Tag Created", description: `Created and added "${newTag.name}"` });
                                                            } catch (error) {
                                                                toast({
                                                                    title: "Error",
                                                                    description: error instanceof Error ? error.message : "Failed to create tag",
                                                                    variant: "destructive"
                                                                });
                                                            }
                                                        }}
                                                        data-testid="button-create-new-tag"
                                                    >
                                                        <Plus className="h-4 w-4" />
                                                        <span>Create "{(saveKnowledgeTagSearch ?? "").trim()}"</span>
                                                    </button>
                                                ) : (
                                                    <span className="text-sm text-muted-foreground py-2">No tags found. Type to create one.</span>
                                                )}
                                            </CommandEmpty>
                                            <CommandGroup>
                                                {allTags
                                                    .filter(tag =>
                                                        !newDocTagIds.includes(tag.id) &&
                                                        tag.name.toLowerCase().includes((saveKnowledgeTagSearch ?? "").toLowerCase())
                                                    )
                                                    .map((tag) => (
                                                        <CommandItem
                                                            key={tag.id}
                                                            value={tag.name}
                                                            onSelect={() => {
                                                                setNewDocTagIds(prev => [...prev, tag.id]);
                                                                setSaveKnowledgeTagSearch("");
                                                            }}
                                                            className="cursor-pointer"
                                                            data-testid={`tag-option-${tag.id}`}
                                                        >
                                                            <Tags className="h-4 w-4 mr-2" style={{ color: tag.color || undefined }} />
                                                            <span>{tag.name}</span>
                                                            {newDocTagIds.includes(tag.id) && (
                                                                <Check className="h-4 w-4 ml-auto" />
                                                            )}
                                                        </CommandItem>
                                                    ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div>
                            <Label>Content Preview</Label>
                            <div className="mt-2 p-3 bg-muted rounded-md text-sm">
                                <h4 className="font-semibold text-base mb-2 line-clamp-1">{saveKnowledgeTitle ?? ""}</h4>
                                <p className="text-muted-foreground line-clamp-2">
                                    {(() => {
                                        // Extract first 1-2 meaningful sentences from HTML content
                                        const textContent = (saveKnowledgeContent ?? "")
                                            .replace(/<[^>]+>/g, ' ')  // Remove HTML tags
                                            .replace(/\s+/g, ' ')       // Normalize whitespace
                                            .trim();
                                        // Find first 2 sentences or first 200 chars
                                        const sentences = textContent.match(/[^.!?]+[.!?]+/g) || [textContent];
                                        const preview = sentences.slice(0, 2).join(' ').substring(0, 200);
                                        return preview + (textContent.length > preview.length ? '...' : '');
                                    })()}
                                </p>
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="mt-6">
                        <Button
                            variant="outline"
                            onClick={() => setSaveKnowledgeDialogOpen(false)}
                            data-testid="button-cancel-save-knowledge"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmSaveToKnowledge}
                            disabled={savingToKnowledge !== null}
                            data-testid="button-confirm-save-knowledge"
                        >
                            {savingToKnowledge ? (
                                <>
                                    <div
                                        className="h-4 w-4 mr-1 border-2 border-current border-t-transparent rounded-full animate-spin"/>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <BookmarkPlus className="h-4 w-4 mr-1"/>
                                    Save as Doc
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
                )}
            </Dialog>

            {/* Google Drive Picker Dialog */}
            <Dialog open={showDrivePicker} onOpenChange={setShowDrivePicker}>
                {showDrivePicker && (
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
                    <DialogHeader>
                        <DialogTitle>Pick from Google Drive</DialogTitle>
                        <DialogDescription>
                            Select files from your Google Drive to attach to the chat
                        </DialogDescription>
                    </DialogHeader>
                    <GoogleDrivePicker
                        multiple={true}
                        onFilesSelected={async (files) => {
                            for (const file of files) {
                                try {
                                    // Skip files that don't have a URL (e.g. text-only files)
                                    if (!('url' in file)) continue;
                                    const response = await fetch(file.url);
                                    const blob = await response.blob();
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                        const base64data = reader.result as string;
                                        if (base64data) {
                                            setFileAttached(prev => [...prev, {
                                                type: getContentType(blob.type),
                                                data: base64data,
                                                filename: file.filename
                                            }]);
                                        }
                                    };
                                    reader.readAsDataURL(blob);
                                } catch (error) {
                                    console.error("Error processing Drive file:", error);
                                    toast({
                                        title: "Error",
                                        description: `Failed to load file: ${file.filename}`,
                                        variant: "destructive",
                                    });
                                }
                            }
                            setShowDrivePicker(false);
                            toast({
                                title: "Files attached",
                                description: `${files.length} file(s) from Google Drive attached`,
                            });
                        }}
                    />
                </DialogContent>
                )}
            </Dialog>

            {/* Thread Linking Dialog */}
            {currentThreadId && thread && threadLinkingDialogOpen && (
                <ThreadLinkingDialog
                    open={threadLinkingDialogOpen}
                    onOpenChange={setThreadLinkingDialogOpen}
                    linkedSpaceIds={thread.linkedSpaceIds || []}
                    topicId={thread.topicId || null}
                    tags={thread.tags || []}
                    onUpdateSpaces={(spaceIds) => {
                        updateThreadMutation.mutate({
                            id: currentThreadId,
                            data: { linkedSpaceIds: spaceIds }
                        });
                    }}
                    onUpdateTopic={(topicId) => {
                        updateThreadMutation.mutate({
                            id: currentThreadId,
                            data: { topicId }
                        });
                    }}
                    onUpdateTags={(tags) => {
                        updateThreadMutation.mutate({
                            id: currentThreadId,
                            data: { tags }
                        });
                    }}
                />
            )}

            {/* PIN Verification Dialog for thread locking */}
            {isPinDialogOpen && (
                <PinVerificationDialog
                    open={isPinDialogOpen}
                    onOpenChange={setIsPinDialogOpen}
                    onSuccess={() => {
                        if (currentThreadId && thread) {
                            const shouldLock = !thread.isLocked;
                            toggleLockMutation.mutate({threadId: currentThreadId, lock: shouldLock});
                        }
                    }}
                    title={thread?.isLocked ? "Unlock Thread" : "Lock Thread"}
                    description="Enter your PIN to continue"
                />
            )}

            <ShareThreadDialog
                open={shareThreadDialogOpen}
                onOpenChange={setShareThreadDialogOpen}
                threadId={currentThreadId ?? ""}
                threadName={thread?.title || "Untitled Chat"}
            />

            <ActionPlanDialog
                open={actionPlanDialogOpen}
                onOpenChange={setActionPlanDialogOpen}
                onCreate={handleCreateActionPlan}
            />

            <SummarizeArchiveDialog
                open={summarizeDialogOpen}
                onOpenChange={setSummarizeDialogOpen}
                onSummarize={handleSummarizeArchive}
                hasAssistantMessage={hasAssistantMessages}
            />

            <AlertDialog open={deleteThreadConfirmOpen} onOpenChange={setDeleteThreadConfirmOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this thread?</AlertDialogTitle>
                        <AlertDialogDescription>
                            "{thread?.title || "Untitled Chat"}" and all of its messages will be
                            permanently deleted. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeletingThread}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                void handleConfirmDeleteThread();
                            }}
                            disabled={isDeletingThread}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="button-confirm-delete-thread"
                        >
                            {isDeletingThread ? "Deleting..." : "Delete"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Selection Toolbar - appears on text selection */}
            <SelectionToolbar
                onClipToSidebar={handleClipToSidebar}
            />

            {/* Fullscreen Image Viewer */}
            <Dialog open={!!fullscreenImage} onOpenChange={(open) => !open && setFullscreenImage(null)}>
                {!!fullscreenImage && (
                <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 overflow-hidden bg-black/95">
                    <DialogHeader className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-black/60 to-transparent">
                        <DialogTitle className="text-white">{fullscreenImage?.alt || "Image"}</DialogTitle>
                    </DialogHeader>
                    <div className="flex items-center justify-center w-full h-full min-h-[60vh]">
                        {fullscreenImage && (
                            <img
                                src={fullscreenImage.url}
                                alt={fullscreenImage.alt}
                                className="max-w-full max-h-[90vh] object-contain"
                                data-testid="fullscreen-image"
                            />
                        )}
                    </div>
                    <div className="absolute bottom-4 right-4 flex gap-2">
                        <Button
                            variant="secondary"
                            onClick={async () => {
                                if (!fullscreenImage) return;
                                try {
                                    const response = await fetch(fullscreenImage.url);
                                    const blob = await response.blob();
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `generated-image-${Date.now()}.png`;
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                    window.URL.revokeObjectURL(url);
                                    toast({ title: "Image downloaded", description: "The image has been saved to your downloads." });
                                } catch {
                                    window.open(fullscreenImage.url, '_blank');
                                }
                            }}
                            data-testid="button-download-fullscreen"
                        >
                            <Download className="h-4 w-4 mr-2" />
                            Download
                        </Button>
                    </div>
                </DialogContent>
                )}
            </Dialog>

            <AlertDialog
                open={pendingRecoveryDraft != null}
                onOpenChange={(open) => {
                    if (!open) handleDismissPendingRecovery();
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Recover unsaved conversation?</AlertDialogTitle>
                        <AlertDialogDescription>
                            A conversation was stored in your browser but not confirmed in the database.
                            {pendingRecoveryDraft ? (
                                <>
                                    {" "}
                                    Preview: &ldquo;{getFirstUserMessagePreview(pendingRecoveryDraft)}&rdquo;
                                </>
                            ) : null}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleDismissPendingRecovery}>Not now</AlertDialogCancel>
                        <AlertDialogAction onClick={handleRecoverPendingDraft}>
                            Recover and save
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
        </ChatWorkspaceErrorBoundary>
    );
}
